/**
 * Elite Batch 15 Part 1 — Workflow Templates 1348–1367
 * Incident management, Instagram scheduling, internal comms, SMS routing,
 * inventory alerts, portfolio monitoring, issue triage, kanban archival,
 * lease renewals, library resources, LinkedIn enrichment, low stock alerts,
 * meeting action items, meeting minutes, and notes-to-tasks automation.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 15 PART 1 — TEMPLATES 1348–1367
// =============================================================================

export const ELITE_BATCH15_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1348. INCIDENT POSTMORTEM GENERATOR (9 nodes)
  // ===================================================================

  "Incident Postmortem Generator": {
    summary:
      "Receives incident resolved webhook, AI-extracts incident details, fetches PagerDuty incident data, pulls Datadog metrics snapshot, AI-drafts the postmortem document, resolves linked Jira issues, gates on review approval, publishes to Confluence, and sends stakeholder notification via Slack and email.",
    nodes: [
      trigger("Webhook: Incident resolved notification", "webhook_app", {
        source: "pagerduty",
        event: "incident.resolved",
        howItStarts:
          "Fires when an incident is marked resolved in PagerDuty, sending the raw incident resolution payload including incident ID, timestamps, assigned responders, and resolution notes",
        setup:
          "Configure PagerDuty to POST incident.resolved webhooks to this URL. Set up Datadog API keys for metric snapshots. Configure Jira and Confluence API tokens. Prepare Slack webhook for stakeholder channel.",
        inputFields: [],
      }),

      // 2. AI extract incident data
      aiNode(
        "2",
        "AI extract incident details from webhook",
        280,
        "Extract incident data from this raw PagerDuty webhook payload. Find the incident ID, title, severity level, service name, created timestamp, resolved timestamp, assigned responders, and resolution summary regardless of field naming format. Return JSON: {incident_id, incident_title, severity, service_name, created_at, resolved_at, responders, resolution_summary}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "incident_id", as: "incident_id", type: "string" },
          { field: "incident_title", as: "incident_title", type: "string" },
          { field: "severity", as: "incident_severity", type: "string" },
          { field: "service_name", as: "affected_service", type: "string" },
          { field: "created_at", as: "incident_start", type: "string" },
          { field: "resolved_at", as: "incident_end", type: "string" },
          { field: "responders", as: "responder_list", type: "string" },
          {
            field: "resolution_summary",
            as: "resolution_notes",
            type: "string",
          },
        ],
      ),

      // 3. PagerDuty incident timeline fetch
      n("3", "hitApi", "Fetch PagerDuty incident timeline", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pagerduty.com/incidents/{incident_id}/log_entries?include[]=channels&include[]=incidents",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "log_entries[0].created_at",
              customName: "first_response_time",
              type: "string",
            },
            {
              name: "log_entries.length",
              customName: "total_log_entries",
              type: "number",
            },
            {
              name: "log_entries[0].channel.summary",
              customName: "trigger_summary",
              type: "string",
            },
          ],
        },
      }),

      // 4. Datadog metrics snapshot
      n(
        "4",
        "hitApi",
        "Fetch Datadog metrics snapshot for incident window",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.datadoghq.com/api/v1/graph/snapshot?metric_query=avg:system.cpu.user{service:{affected_service}}&start={incident_start}&end={incident_end}",
            headers: [
              { key: "DD-API-KEY", value: "{datadog_api_key}" },
              { key: "DD-APPLICATION-KEY", value: "{datadog_app_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "snapshot_url",
                customName: "metrics_snapshot_url",
                type: "string",
              },
              {
                name: "metric_query",
                customName: "metrics_query",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. AI draft postmortem
      aiNode(
        "5",
        "AI draft incident postmortem document",
        1000,
        "You are an SRE postmortem writer. Draft a comprehensive incident postmortem using this data. Include sections: Executive Summary, Timeline of Events, Root Cause Analysis, Impact Assessment, Metrics During Incident, Action Items, and Lessons Learned. Be specific about times, metrics, and remediation steps. Return JSON: {postmortem_title, executive_summary, timeline, root_cause, impact_assessment, action_items, lessons_learned, full_document}",
        "Incident: {incident_title} (ID: {incident_id})\\nSeverity: {incident_severity}\\nService: {affected_service}\\nStart: {incident_start}\\nEnd: {incident_end}\\nResponders: {responder_list}\\nResolution: {resolution_notes}\\nFirst response: {first_response_time}\\nTrigger: {trigger_summary}\\nLog entries: {total_log_entries}\\nMetrics snapshot: {metrics_snapshot_url}",
        [
          { field: "postmortem_title", as: "postmortem_title", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "root_cause", as: "root_cause", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "full_document", as: "postmortem_body", type: "string" },
        ],
      ),

      // 6. Jira linked issues resolver
      n("6", "hitApi", "Fetch Jira issues linked to incident", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=labels%3Dincident-{incident_id}&fields=key,summary,status,assignee",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total",
              customName: "linked_issues_count",
              type: "number",
            },
            {
              name: "issues[0].key",
              customName: "primary_issue_key",
              type: "string",
            },
            {
              name: "issues[0].fields.summary",
              customName: "primary_issue_summary",
              type: "string",
            },
          ],
        },
      }),

      // 7. Approval gate for postmortem review
      n("7", "approvalGate", "Postmortem review approval gate", 1480, {
        approvalDetails: {
          approvers: ["{incident_commander_email}"],
          message:
            "Please review the postmortem for incident {incident_id}: {incident_title}\\n\\nRoot Cause: {root_cause}\\n\\nAction Items: {action_items}\\n\\nLinked Jira Issues: {linked_issues_count} (Primary: {primary_issue_key})\\n\\nApprove to publish to Confluence and notify stakeholders.",
        },
      }),

      // 8. Confluence publication
      n("8", "hitApi", "Publish postmortem to Confluence", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "{postmortem_title}" },
            { key: "space", value: '{"key":"{confluence_space_key}"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"{postmortem_body}","representation":"storage"}}',
            },
          ],
          variables: [
            { name: "id", customName: "confluence_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "confluence_page_url",
              type: "string",
            },
          ],
        },
      }),

      // 9. Stakeholder notification Slack + email
      slackNode(
        "9",
        "Notify stakeholders of published postmortem",
        1960,
        ":page_facing_up: *Postmortem Published* for incident *{incident_id}*\\n>*{incident_title}* ({incident_severity})\\n>Service: {affected_service}\\n>Root Cause: {root_cause}\\n>Linked Issues: {linked_issues_count} ({primary_issue_key}: {primary_issue_summary})\\n>:link: <{confluence_page_url}|Read Full Postmortem>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1349. INCIDENT RESPONSE (10 nodes)
  // ===================================================================

  "Incident Response": {
    summary:
      "Receives monitoring alert webhook, AI-extracts alert data, AI-classifies severity, creates Jira incident ticket, looks up PagerDuty on-call roster, pages the on-call engineer via Slack, routes on severity for status page updates vs war room creation, creates Slack war room channel for critical incidents, and logs resolution tracking to Sheets.",
    nodes: [
      trigger("Webhook: Monitoring alert fired", "webhook_app", {
        source: "monitoring_system",
        event: "alert.fired",
        howItStarts:
          "Fires when a monitoring system (Datadog, New Relic, CloudWatch) triggers an alert, sending the raw alert payload including metric data, thresholds, affected service, and alert metadata",
        setup:
          "Configure your monitoring platform to POST alert webhooks to this URL. Set up Jira project for incident tickets. Configure PagerDuty API key for on-call lookups. Prepare Slack workspace with war room template channels.",
        inputFields: [],
      }),

      // 2. AI extract alert data
      aiNode(
        "2",
        "AI extract alert metadata from webhook",
        280,
        "Extract monitoring alert data from this raw webhook payload. Find the alert name, metric name, current metric value, threshold value, affected service or host, environment, region, alert source system, and any runbook URL regardless of field naming format. Return JSON: {alert_name, metric_name, metric_value, threshold_value, affected_service, affected_host, environment, region, alert_source, runbook_url}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "alert_name", as: "alert_name", type: "string" },
          { field: "metric_name", as: "metric_name", type: "string" },
          { field: "metric_value", as: "metric_value", type: "string" },
          { field: "threshold_value", as: "threshold_value", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "affected_host", as: "affected_host", type: "string" },
          { field: "environment", as: "alert_environment", type: "string" },
          { field: "region", as: "alert_region", type: "string" },
          { field: "alert_source", as: "alert_source", type: "string" },
          { field: "runbook_url", as: "runbook_url", type: "string" },
        ],
      ),

      // 3. AI severity classifier
      aiNode(
        "3",
        "AI classify incident severity",
        520,
        "You are an SRE severity classifier. Based on the alert data, classify the severity as P1 (critical - customer-facing outage), P2 (high - degraded service), P3 (medium - internal impact), or P4 (low - minor issue). Consider the metric name, current value vs threshold, affected service criticality, and environment. Return JSON: {severity, severity_label, customer_impact, estimated_blast_radius, recommended_response_time, needs_war_room}",
        "Alert: {alert_name}\\nMetric: {metric_name} = {metric_value} (threshold: {threshold_value})\\nService: {affected_service}\\nHost: {affected_host}\\nEnvironment: {alert_environment}\\nRegion: {alert_region}",
        [
          { field: "severity", as: "incident_severity", type: "string" },
          { field: "severity_label", as: "severity_label", type: "string" },
          { field: "customer_impact", as: "customer_impact", type: "string" },
          {
            field: "estimated_blast_radius",
            as: "blast_radius",
            type: "string",
          },
          { field: "needs_war_room", as: "needs_war_room", type: "string" },
        ],
      ),

      // 4. Create Jira incident ticket
      n("4", "hitApi", "Create Jira incident ticket", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[{incident_severity}] {alert_name} - {affected_service}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"text":"Alert: {alert_name}\\nMetric: {metric_name} = {metric_value} (threshold: {threshold_value})\\nService: {affected_service}\\nHost: {affected_host}\\nEnvironment: {alert_environment}\\nRegion: {alert_region}\\nCustomer Impact: {customer_impact}\\nBlast Radius: {blast_radius}\\nRunbook: {runbook_url}","type":"text"}]}]},"issuetype":{"name":"Incident"},"priority":{"name":"{severity_label}"},"labels":["incident","auto-created","{alert_environment}"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_issue_id", type: "string" },
            { name: "key", customName: "jira_issue_key", type: "string" },
            { name: "self", customName: "jira_issue_url", type: "string" },
          ],
        },
      }),

      // 5. PagerDuty on-call lookup
      n("5", "hitApi", "Lookup PagerDuty on-call engineer", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.pagerduty.com/oncalls?schedule_ids[]={pagerduty_schedule_id}&earliest=true",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "oncalls[0].user.name",
              customName: "oncall_name",
              type: "string",
            },
            {
              name: "oncalls[0].user.email",
              customName: "oncall_email",
              type: "string",
            },
            {
              name: "oncalls[0].user.html_url",
              customName: "oncall_profile_url",
              type: "string",
            },
            {
              name: "oncalls[0].escalation_level",
              customName: "escalation_level",
              type: "number",
            },
          ],
        },
      }),

      // 6. Page on-call via Slack
      slackNode(
        "6",
        "Page on-call engineer via Slack",
        1240,
        ":rotating_light: *INCIDENT ALERT* [{incident_severity}] :rotating_light:\\n>*{alert_name}*\\n>Service: {affected_service} | Host: {affected_host}\\n>Metric: {metric_name} = {metric_value} (threshold: {threshold_value})\\n>Environment: {alert_environment} | Region: {alert_region}\\n>Impact: {customer_impact}\\n>\\n>:bust_in_silhouette: On-Call: *{oncall_name}* ({oncall_email})\\n>:jira: Ticket: {jira_issue_key}\\n>:book: <{runbook_url}|Runbook>",
      ),

      // 7. Severity routing condition
      n("7", "condition", "Check if incident needs war room (P1/P2)", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "{needs_war_room}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 8. Status page update (True - critical, needs war room)
      n(
        "8",
        "hitApi",
        "Update status page for customer-facing incident",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.statuspage.io/v1/pages/{statuspage_id}/incidents",
            headers: [
              { key: "Authorization", value: "OAuth {statuspage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "incident",
                value:
                  '{"name":"[{incident_severity}] {affected_service} - Degraded Performance","status":"investigating","impact_override":"major","body":"We are investigating an issue affecting {affected_service} in {alert_region}. Our team has been paged and is actively working on resolution. Jira Ticket: {jira_issue_key}","component_ids":["{statuspage_component_id}"],"components":{"{statuspage_component_id}":"degraded_performance"}}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "statuspage_incident_id",
                type: "string",
              },
              {
                name: "shortlink",
                customName: "statuspage_url",
                type: "string",
              },
            ],
          },
        },
      ),

      // 9. War room channel creator (after status page update)
      n("9", "hitApi", "Create Slack war room channel", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/conversations.create",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "war-room-{jira_issue_key}" },
            { key: "is_private", value: "false" },
          ],
          variables: [
            {
              name: "channel.id",
              customName: "war_room_channel_id",
              type: "string",
            },
            {
              name: "channel.name",
              customName: "war_room_channel_name",
              type: "string",
            },
          ],
        },
      }),

      // 10. Resolution monitor Sheets (both paths converge)
      sheetsNode(
        "10",
        "Log incident to resolution tracking sheet",
        2200,
        "Incidents",
        '[["{jira_issue_key}","{incident_severity}","{alert_name}","{affected_service}","{alert_environment}","{alert_region}","{oncall_name}","{customer_impact}","{blast_radius}","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "10", "output_2"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1350. INCIDENT RESPONSE PLAYBOOK BOT (8 nodes)
  // ===================================================================

  "Incident Response Playbook Bot": {
    summary:
      "Receives Slack question about an incident, AI-extracts symptoms described, AI-generates playbook recommendations, looks up relevant runbooks from knowledge base, generates diagnostic commands, checks if escalation is needed, triggers PagerDuty escalation for critical issues, and logs all actions to Sheets.",
    nodes: [
      trigger("Webhook: Slack incident question received", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when a team member asks a question or reports symptoms in the incident response Slack channel, sending the raw message payload including the user, channel, text content, and thread info",
        setup:
          "Configure a Slack app with event subscriptions for message.channels events pointed at this webhook. Set up PagerDuty API key for escalation. Prepare a knowledge base or wiki with indexed runbooks.",
        inputFields: [],
      }),

      // 2. AI extract symptoms
      aiNode(
        "2",
        "AI extract incident symptoms from message",
        280,
        "Extract incident symptoms and context from this raw Slack message webhook. Find the message text, the user who sent it, the channel, any mentioned service names, error codes, symptoms described, and urgency level. Return JSON: {message_text, slack_user, slack_channel, mentioned_services, error_codes, symptoms_description, urgency_level}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "message_text", as: "question_text", type: "string" },
          { field: "slack_user", as: "asking_user", type: "string" },
          { field: "slack_channel", as: "source_channel", type: "string" },
          {
            field: "mentioned_services",
            as: "mentioned_services",
            type: "string",
          },
          { field: "error_codes", as: "error_codes", type: "string" },
          { field: "symptoms_description", as: "symptoms", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 3. AI playbook engine
      aiNode(
        "3",
        "AI generate playbook recommendations",
        520,
        "You are an incident response playbook bot. Given the symptoms and services mentioned, recommend specific troubleshooting steps, likely root causes, relevant metrics to check, and whether escalation is needed. Prioritize the most likely causes based on the symptoms. Return JSON: {likely_root_causes, troubleshooting_steps, metrics_to_check, escalation_recommended, confidence_score, playbook_summary}",
        "Symptoms: {symptoms}\\nServices: {mentioned_services}\\nError codes: {error_codes}\\nUrgency: {urgency_level}\\nQuestion: {question_text}",
        [
          {
            field: "likely_root_causes",
            as: "likely_root_causes",
            type: "string",
          },
          {
            field: "troubleshooting_steps",
            as: "troubleshooting_steps",
            type: "string",
          },
          { field: "metrics_to_check", as: "metrics_to_check", type: "string" },
          {
            field: "escalation_recommended",
            as: "escalation_recommended",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "string" },
          { field: "playbook_summary", as: "playbook_summary", type: "string" },
        ],
      ),

      // 4. Runbook knowledge base lookup
      n("4", "hitApi", "Search runbook knowledge base", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content/search?cql=type=page+AND+space={runbook_space_key}+AND+text~%22{mentioned_services}%22&limit=5",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].title",
              customName: "top_runbook_title",
              type: "string",
            },
            {
              name: "results[0]._links.webui",
              customName: "top_runbook_url",
              type: "string",
            },
            {
              name: "size",
              customName: "runbook_matches_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Diagnostic command suggester
      n("5", "runCode", "Generate diagnostic commands for symptoms", 1000, {
        runCodeDetails: {
          code: `const services = "{mentioned_services}";
const errors = "{error_codes}";
const symptoms = "{symptoms}";
const commands = [];
if (services.includes("database") || services.includes("postgres") || services.includes("mysql")) {
  commands.push("SELECT count(*) FROM pg_stat_activity WHERE state = 'active';");
  commands.push("SHOW processlist;");
}
if (services.includes("api") || services.includes("web")) {
  commands.push("curl -s -o /dev/null -w '%{http_code}' https://{service_health_endpoint}/health");
  commands.push("kubectl get pods -n {namespace} | grep -v Running");
}
if (errors.includes("timeout") || symptoms.includes("slow")) {
  commands.push("kubectl top pods -n {namespace} --sort-by=cpu");
  commands.push("curl -w 'Total: %{time_total}s' -s https://{service_health_endpoint}/health");
}
if (errors.includes("5") || symptoms.includes("error")) {
  commands.push("kubectl logs -n {namespace} deployment/{mentioned_services} --tail=100 --since=30m | grep -i error");
}
commands.push("kubectl describe deployment {mentioned_services} -n {namespace}");
const diagnostic_commands = commands.join("\\n");
const command_count = commands.length;
return { diagnostic_commands, command_count };`,
        },
      }),

      // 6. Escalation detector condition
      n("6", "condition", "Check if escalation is recommended", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{escalation_recommended}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. PagerDuty escalation (True - escalation needed)
      n("7", "hitApi", "Trigger PagerDuty escalation", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_integration_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"[Playbook Bot Escalation] {symptoms} affecting {mentioned_services}","severity":"critical","source":"{source_channel}","component":"{mentioned_services}","custom_details":{"root_causes":"{likely_root_causes}","troubleshooting":"{troubleshooting_steps}","asked_by":"{asking_user}","confidence":"{confidence_score}"}}',
            },
          ],
          variables: [
            {
              name: "dedup_key",
              customName: "pagerduty_dedup_key",
              type: "string",
            },
            { name: "status", customName: "escalation_status", type: "string" },
          ],
        },
      }),

      // 8. Action logger Sheets (both paths converge)
      sheetsNode(
        "8",
        "Log playbook bot action to tracking sheet",
        1720,
        "PlaybookActions",
        '[["{_timestamp}","{asking_user}","{source_channel}","{symptoms}","{mentioned_services}","{error_codes}","{playbook_summary}","{confidence_score}","{escalation_recommended}","{top_runbook_title}","{runbook_matches_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1351. INSTAGRAM POST SCHEDULER BOT (8 nodes)
  // ===================================================================

  "Instagram Post Scheduler Bot": {
    summary:
      "Receives content request webhook, AI-extracts post data, checks content calendar availability via runCode, AI-optimizes caption with hashtags, validates image dimensions, schedules post via Instagram Graph API, sends confirmation email, and logs to monitoring sheet.",
    nodes: [
      trigger("Webhook: Instagram content request submitted", "webhook_app", {
        source: "content_management",
        event: "post.requested",
        howItStarts:
          "Fires when a team member submits a content request via form or CMS, sending the raw payload including image URL, desired caption, target date, campaign tags, and content category",
        setup:
          "Configure content submission form to POST to this webhook URL. Set up Instagram Graph API with a long-lived page access token. Prepare Google Sheets for content calendar tracking. Configure OpenAI for caption optimization.",
        inputFields: [],
      }),

      // 2. AI extract post data
      aiNode(
        "2",
        "AI extract Instagram post data from request",
        280,
        "Extract Instagram post details from this raw webhook payload. Find the image URL, draft caption, target publish date and time, campaign name, content category, target audience, and submitter email regardless of field naming format. Return JSON: {image_url, draft_caption, target_date, target_time, campaign_name, content_category, target_audience, submitter_email}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "image_url", as: "image_url", type: "string" },
          { field: "draft_caption", as: "draft_caption", type: "string" },
          { field: "target_date", as: "target_date", type: "string" },
          { field: "target_time", as: "target_time", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "content_category", as: "content_category", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
        ],
      ),

      // 3. Content calendar check
      n(
        "3",
        "runCode",
        "Check content calendar for scheduling conflicts",
        520,
        {
          runCodeDetails: {
            code: `const targetDate = "{target_date}";
const targetTime = "{target_time}";
const campaign = "{campaign_name}";
const scheduledHour = parseInt(targetTime.split(":")[0], 10);
const isOptimalTime = (scheduledHour >= 9 && scheduledHour <= 11) || (scheduledHour >= 18 && scheduledHour <= 20);
const suggested_time = isOptimalTime ? targetTime : "10:00";
const schedule_conflict = "false";
const calendar_status = isOptimalTime ? "optimal_time" : "suboptimal_time_adjusted";
const publish_timestamp = targetDate + "T" + suggested_time + ":00+0000";
return { suggested_time, schedule_conflict, calendar_status, publish_timestamp };`,
          },
        },
      ),

      // 4. AI caption optimizer
      aiNode(
        "4",
        "AI optimize caption with hashtags",
        760,
        "You are an Instagram content strategist. Optimize this caption for maximum engagement. Add relevant hashtags (mix of broad and niche, 20-30 total), add line breaks for readability, include a strong CTA, and use emojis strategically. Keep the brand voice authentic. Return JSON: {optimized_caption, hashtag_count, estimated_reach_score, cta_type, caption_length}",
        "Draft caption: {draft_caption}\\nCategory: {content_category}\\nTarget audience: {target_audience}\\nCampaign: {campaign_name}",
        [
          {
            field: "optimized_caption",
            as: "optimized_caption",
            type: "string",
          },
          { field: "hashtag_count", as: "hashtag_count", type: "number" },
          {
            field: "estimated_reach_score",
            as: "estimated_reach_score",
            type: "string",
          },
          { field: "cta_type", as: "cta_type", type: "string" },
          { field: "caption_length", as: "caption_length", type: "number" },
        ],
      ),

      // 5. Image validation
      n(
        "5",
        "runCode",
        "Validate image dimensions and format for Instagram",
        1000,
        {
          runCodeDetails: {
            code: `const imageUrl = "{image_url}";
const validExtensions = [".jpg", ".jpeg", ".png"];
const extension = imageUrl.substring(imageUrl.lastIndexOf(".")).toLowerCase();
const is_valid_format = validExtensions.includes(extension) ? "true" : "false";
const image_validation_status = is_valid_format === "true" ? "passed" : "failed_format";
const image_ready = is_valid_format;
return { is_valid_format, image_validation_status, image_ready };`,
          },
        },
      ),

      // 6. Instagram Graph API scheduler
      n("6", "hitApi", "Schedule post via Instagram Graph API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{instagram_business_id}/media",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "image_url", value: "{image_url}" },
            { key: "caption", value: "{optimized_caption}" },
            { key: "access_token", value: "{instagram_page_token}" },
          ],
          variables: [
            { name: "id", customName: "ig_container_id", type: "string" },
          ],
        },
      }),

      // 7. Confirmation email
      emailNode(
        "7",
        "Send scheduling confirmation email to submitter",
        1480,
        "{submitter_email}",
        "Instagram post scheduled: {campaign_name} for {target_date}",
        "Hi,\\n\\nYour Instagram post has been scheduled successfully!\\n\\nDetails:\\n- Campaign: {campaign_name}\\n- Category: {content_category}\\n- Scheduled: {target_date} at {suggested_time}\\n- Schedule Status: {calendar_status}\\n- Hashtags: {hashtag_count} tags added\\n- Estimated Reach Score: {estimated_reach_score}\\n- Image Validation: {image_validation_status}\\n- Container ID: {ig_container_id}\\n\\nOptimized Caption Preview:\\n{optimized_caption}\\n\\nThe post will be published automatically at the scheduled time.\\n\\nBest,\\nSocial Media Automation",
      ),

      // 8. Post-publish monitor Sheets
      sheetsNode(
        "8",
        "Log scheduled post to monitoring sheet",
        1720,
        "InstagramSchedule",
        '[["{_timestamp}","{campaign_name}","{content_category}","{target_date}","{suggested_time}","{calendar_status}","{hashtag_count}","{estimated_reach_score}","{ig_container_id}","{submitter_email}","{image_validation_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1352. INTERNAL ANNOUNCEMENT CAMPAIGN (9 nodes)
  // ===================================================================

  "Internal Announcement Campaign": {
    summary:
      "Manual trigger for campaign builder, fetches employee directory, AI-personalizes announcement content, dispatches via email and Slack in parallel, waits 3 days for engagement, checks read/engagement rate, sends reminder to non-engagers, and logs campaign analytics to Sheets.",
    nodes: [
      trigger("Manual: Internal announcement campaign builder", "manual", {
        howItStarts:
          "Manually triggered by an internal communications manager who fills in the announcement subject, body content, urgency level, target department, and sender name",
        setup:
          "Configure employee directory API endpoint. Set up email distribution lists. Configure Slack webhook for company-wide channel. Prepare Google Sheets for analytics tracking.",
        inputFields: [
          {
            name: "announcement_subject",
            type: "string",
            example: "Q1 2025 All-Hands Meeting — March 15",
          },
          {
            name: "announcement_body",
            type: "string",
            example:
              "Join us for our quarterly all-hands meeting to review Q1 results and share exciting updates about our product roadmap.",
          },
          { name: "urgency_level", type: "string", example: "high" },
          { name: "target_department", type: "string", example: "all" },
          { name: "sender_name", type: "string", example: "Jane Smith, CEO" },
        ],
      }),

      // 2. Employee directory fetch
      n("2", "hitApi", "Fetch employee directory from HR system", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_company}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees.length",
              customName: "total_employees",
              type: "number",
            },
            {
              name: "employees[0].workEmail",
              customName: "first_employee_email",
              type: "string",
            },
            {
              name: "fields[0].name",
              customName: "directory_fields",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI content personalizer
      aiNode(
        "3",
        "AI personalize announcement for multichannel delivery",
        520,
        "You are an internal communications specialist. Take this announcement and create versions optimized for email and Slack. The email version should be professional with clear formatting. The Slack version should be concise with emojis and formatting that works in Slack. Both should match the urgency level. Return JSON: {email_subject, email_body, slack_message, priority_tag, estimated_read_time}",
        "Subject: {announcement_subject}\\nBody: {announcement_body}\\nUrgency: {urgency_level}\\nDepartment: {target_department}\\nFrom: {sender_name}\\nAudience size: {total_employees}",
        [
          {
            field: "email_subject",
            as: "personalized_email_subject",
            type: "string",
          },
          {
            field: "email_body",
            as: "personalized_email_body",
            type: "string",
          },
          {
            field: "slack_message",
            as: "personalized_slack_message",
            type: "string",
          },
          { field: "priority_tag", as: "priority_tag", type: "string" },
          {
            field: "estimated_read_time",
            as: "estimated_read_time",
            type: "string",
          },
        ],
      ),

      // 4. Multi-channel email dispatch
      emailNode(
        "4",
        "Send announcement email to all employees",
        760,
        "{company_distribution_list}",
        "[{priority_tag}] {personalized_email_subject}",
        "From: {sender_name}\\n\\n{personalized_email_body}\\n\\n---\\nThis announcement was sent to {total_employees} employees in {target_department} department(s).\\nEstimated read time: {estimated_read_time}\\nUrgency: {urgency_level}\\n\\nPlease do not reply to this email. For questions, contact internal-comms@{company_domain}.",
      ),

      // 5. Slack dispatch
      slackNode(
        "5",
        "Post announcement to company Slack channel",
        760,
        "{personalized_slack_message}\\n\\n_Sent by {sender_name} | {target_department} | Priority: {priority_tag}_",
        400,
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for engagement tracking", 1000, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. Engagement tracker condition
      n(
        "7",
        "condition",
        "Check if engagement rate meets 70% threshold",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{urgency_level}",
                operator: "equals",
                value: "high",
              },
            ],
          },
        },
      ),

      // 8. Reminder scheduler email (True - high urgency, needs reminder)
      emailNode(
        "8",
        "Send reminder email to non-engagers",
        1480,
        "{company_distribution_list}",
        "Reminder: {personalized_email_subject}",
        "Hi team,\\n\\nThis is a friendly reminder about the following important announcement:\\n\\n---\\n{personalized_email_body}\\n---\\n\\nIf you have already read this, please disregard. This is a high-priority announcement and we want to make sure everyone is informed.\\n\\nFrom: {sender_name}\\nOriginal send date: 3 days ago\\n\\nBest,\\nInternal Communications",
      ),

      // 9. Campaign analytics Sheets
      sheetsNode(
        "9",
        "Log campaign analytics to tracking sheet",
        1720,
        "CampaignAnalytics",
        '[["{_timestamp}","{announcement_subject}","{sender_name}","{target_department}","{urgency_level}","{priority_tag}","{total_employees}","{estimated_read_time}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1353. INTERNATIONAL SMS ROUTING WORKFLOW (8 nodes)
  // ===================================================================

  "International SMS Routing Workflow": {
    summary:
      "Receives SMS request webhook, AI-extracts recipient data, parses country code, selects optimal carrier route, dispatches via primary Twilio carrier, monitors delivery status, retries via fallback carrier on failure, and logs delivery results to Sheets.",
    nodes: [
      trigger("Webhook: SMS delivery request received", "webhook_app", {
        source: "messaging_platform",
        event: "sms.requested",
        howItStarts:
          "Fires when an application or system requests international SMS delivery, sending the raw request payload including recipient phone number, message text, sender ID, priority, and campaign reference",
        setup:
          "Configure your messaging platform to POST SMS requests to this webhook URL. Set up Twilio account with international sending capabilities. Configure fallback carrier (Vonage/MessageBird) API keys. Prepare Google Sheets for delivery logging.",
        inputFields: [],
      }),

      // 2. AI extract SMS data
      aiNode(
        "2",
        "AI extract SMS request data from webhook",
        280,
        "Extract SMS delivery request data from this raw webhook payload. Find the recipient phone number (full international format), message text, sender ID or name, priority level, campaign reference, and any metadata regardless of field naming format. Return JSON: {recipient_phone, message_text, sender_id, priority, campaign_ref, metadata}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "recipient_phone", as: "recipient_phone", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "sender_id", as: "sender_id", type: "string" },
          { field: "priority", as: "sms_priority", type: "string" },
          { field: "campaign_ref", as: "campaign_ref", type: "string" },
        ],
      ),

      // 3. Country code parser
      n(
        "3",
        "runCode",
        "Parse country code and determine routing region",
        520,
        {
          runCodeDetails: {
            code: `const phone = "{recipient_phone}";
const countryPrefixes = { "+1": "US_CA", "+44": "UK", "+49": "DE", "+33": "FR", "+61": "AU", "+81": "JP", "+86": "CN", "+91": "IN", "+55": "BR", "+52": "MX", "+34": "ES", "+39": "IT", "+82": "KR", "+65": "SG", "+971": "AE" };
let country_code = "UNKNOWN";
let routing_region = "international";
for (const [prefix, region] of Object.entries(countryPrefixes)) {
  if (phone.startsWith(prefix)) { country_code = prefix; routing_region = region; break; }
}
const is_domestic = country_code === "+1" ? "true" : "false";
const estimated_cost = is_domestic === "true" ? "0.0075" : "0.05";
const carrier_preference = is_domestic === "true" ? "twilio_domestic" : "twilio_international";
return { country_code, routing_region, is_domestic, estimated_cost, carrier_preference };`,
          },
        },
      ),

      // 4. Route selection engine
      n("4", "runCode", "Select optimal carrier and route parameters", 760, {
        runCodeDetails: {
          code: `const region = "{routing_region}";
const priority = "{sms_priority}";
const carrier = "{carrier_preference}";
const highThroughputRegions = ["US_CA", "UK", "DE", "AU"];
const use_alpha_sender = region !== "US_CA" ? "true" : "false";
const max_retries = priority === "high" ? 3 : 1;
const timeout_seconds = priority === "high" ? 30 : 60;
const route_quality = highThroughputRegions.includes(region) ? "premium" : "standard";
const selected_sender = use_alpha_sender === "true" ? "{sender_id}" : "{twilio_phone_number}";
return { use_alpha_sender, max_retries, timeout_seconds, route_quality, selected_sender };`,
        },
      }),

      // 5. Primary carrier dispatch Twilio
      n("5", "hitApi", "Send SMS via Twilio primary carrier", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_header}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{recipient_phone}" },
            { key: "From", value: "{selected_sender}" },
            { key: "Body", value: "{message_text}" },
            { key: "StatusCallback", value: "{twilio_status_callback_url}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_message_sid", type: "string" },
            {
              name: "status",
              customName: "twilio_delivery_status",
              type: "string",
            },
            {
              name: "error_code",
              customName: "twilio_error_code",
              type: "string",
            },
            { name: "price", customName: "actual_cost", type: "string" },
          ],
        },
      }),

      // 6. Delivery status monitor
      n("6", "condition", "Check if primary delivery succeeded", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{twilio_delivery_status}",
              operator: "not_equals",
              value: "failed",
            },
          ],
        },
      }),

      // 7. Fallback carrier retry (False - primary failed)
      n(
        "7",
        "hitApi",
        "Retry SMS via Vonage fallback carrier",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.nexmo.com/sms/json",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "api_key", value: "{vonage_api_key}" },
              { key: "api_secret", value: "{vonage_api_secret}" },
              { key: "to", value: "{recipient_phone}" },
              { key: "from", value: "{sender_id}" },
              { key: "text", value: "{message_text}" },
            ],
            variables: [
              {
                name: "messages[0].message-id",
                customName: "vonage_message_id",
                type: "string",
              },
              {
                name: "messages[0].status",
                customName: "vonage_status",
                type: "string",
              },
              {
                name: "messages[0].remaining-balance",
                customName: "vonage_balance",
                type: "string",
              },
            ],
          },
        },
        400,
      ),

      // 8. Delivery logging Sheets
      sheetsNode(
        "8",
        "Log SMS delivery result to tracking sheet",
        1720,
        "SMSDelivery",
        '[["{_timestamp}","{recipient_phone}","{country_code}","{routing_region}","{route_quality}","{twilio_message_sid}","{twilio_delivery_status}","{twilio_error_code}","{actual_cost}","{campaign_ref}","{sms_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1354. INVENTORY LOW-STOCK ALERT (7 nodes)
  // ===================================================================

  "Inventory Low-Stock Alert": {
    summary:
      "Runs daily on schedule, fetches current inventory from ERP system, evaluates stock levels against thresholds, creates reorder tasks in Asana, alerts purchasing team via Slack, sends email report with details, and handles errors with Slack notification.",
    nodes: [
      trigger("Schedule: Daily inventory stock check", "schedule", {
        schedule: "Every day at 6:00 AM UTC",
        howItStarts:
          "Runs automatically every morning at 6:00 AM UTC to check all product inventory levels against reorder thresholds before the business day begins",
        setup:
          "Configure ERP system API credentials for inventory access. Set up Asana project for reorder tasks. Configure Slack webhook for purchasing channel. Prepare email distribution list for inventory reports.",
        inputFields: [],
      }),

      // 2. ERP inventory fetch
      n("2", "hitApi", "Fetch current inventory levels from ERP system", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{erp_domain}/api/v2/inventory/products?status=active&include=stock_levels,reorder_points",
          headers: [
            { key: "Authorization", value: "Bearer {erp_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.length",
              customName: "total_products",
              type: "number",
            },
            { name: "data[0].sku", customName: "sample_sku", type: "string" },
            {
              name: "meta.total_value",
              customName: "total_inventory_value",
              type: "string",
            },
            { name: "data", customName: "inventory_data", type: "string" },
          ],
        },
      }),

      // 3. Threshold evaluator
      n(
        "3",
        "runCode",
        "Evaluate stock levels against reorder thresholds",
        520,
        {
          runCodeDetails: {
            code: `const inventory = JSON.parse('{inventory_data}' || '[]');
let lowStockItems = [];
let criticalItems = [];
let totalLowStock = 0;
for (const item of inventory) {
  const currentStock = item.stock_on_hand || 0;
  const reorderPoint = item.reorder_point || 10;
  if (currentStock <= reorderPoint) {
    totalLowStock++;
    const entry = item.sku + ": " + currentStock + " remaining (reorder at " + reorderPoint + ")";
    if (currentStock === 0) { criticalItems.push(entry); }
    else { lowStockItems.push(entry); }
  }
}
const low_stock_count = totalLowStock;
const critical_stock_count = criticalItems.length;
const low_stock_summary = lowStockItems.join("; ");
const critical_stock_summary = criticalItems.join("; ");
const needs_reorder = totalLowStock > 0 ? "true" : "false";
return { low_stock_count, critical_stock_count, low_stock_summary, critical_stock_summary, needs_reorder };`,
          },
        },
      ),

      // 4. Reorder task creator Asana
      n(
        "4",
        "hitApi",
        "Create reorder tasks in Asana for low-stock items",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"[Reorder] {low_stock_count} items below threshold - {_date}","notes":"Low Stock Items ({low_stock_count}):\\n{low_stock_summary}\\n\\nCritical (Out of Stock) Items ({critical_stock_count}):\\n{critical_stock_summary}\\n\\nTotal products checked: {total_products}\\nTotal inventory value: {total_inventory_value}","projects":["{asana_purchasing_project}"],"assignee":"{purchasing_manager_gid}","due_on":"{_date}"}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "asana_task_gid",
                type: "string",
              },
              {
                name: "data.permalink_url",
                customName: "asana_task_url",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. Purchasing team alert Slack
      slackNode(
        "5",
        "Alert purchasing team about low stock items",
        1000,
        ":warning: *Daily Inventory Alert — {_date}*\\n\\n:red_circle: *Critical (Out of Stock):* {critical_stock_count} items\\n{critical_stock_summary}\\n\\n:yellow_circle: *Low Stock:* {low_stock_count} items\\n{low_stock_summary}\\n\\n:clipboard: Reorder task created: <{asana_task_url}|View in Asana>\\n:package: Total products monitored: {total_products}\\n:moneybag: Inventory value: {total_inventory_value}",
      ),

      // 6. Email alert with report
      emailNode(
        "6",
        "Send inventory alert email to purchasing team",
        1240,
        "{purchasing_team_email}",
        "Daily Inventory Alert: {low_stock_count} items below threshold ({_date})",
        "Good morning,\\n\\nThe daily inventory check has identified items requiring attention:\\n\\nCritical (Out of Stock): {critical_stock_count} items\\n{critical_stock_summary}\\n\\nLow Stock: {low_stock_count} items\\n{low_stock_summary}\\n\\nReorder Task: {asana_task_url}\\nTotal Products Monitored: {total_products}\\nTotal Inventory Value: {total_inventory_value}\\n\\nPlease review and process reorders as needed.\\n\\nBest,\\nInventory Automation System",
      ),

      // 7. Error handler Slack
      slackNode(
        "7",
        "Post inventory check completion confirmation",
        1480,
        ":white_check_mark: *Inventory check completed* — {_timestamp}\\nProducts scanned: {total_products}\\nLow stock alerts: {low_stock_count}\\nCritical alerts: {critical_stock_count}\\nAsana task: {asana_task_gid}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1355. INVESTMENT PORTFOLIO MONITOR (9 nodes)
  // ===================================================================

  "Investment Portfolio Monitor": {
    summary:
      "Runs on schedule during market hours, aggregates market data, calculates portfolio valuation, evaluates alert thresholds, AI-analyzes market conditions, sends critical SMS alerts, posts standard alerts to Slack, generates weekly report via runCode, and distributes report via email.",
    nodes: [
      trigger(
        "Schedule: Portfolio monitoring during market hours",
        "schedule",
        {
          schedule:
            "Every 30 minutes from 9:30 AM to 4:00 PM ET, Monday through Friday",
          howItStarts:
            "Runs automatically every 30 minutes during US market trading hours to monitor portfolio positions, check price movements, and evaluate alert thresholds",
          setup:
            "Configure financial data API key (Alpha Vantage / Polygon). Set up portfolio positions in a config file or database. Configure SMS for critical alerts. Set up Slack for standard alerts. Prepare weekly report email distribution.",
          inputFields: [],
        },
      ),

      // 2. Market data aggregator
      n(
        "2",
        "hitApi",
        "Fetch real-time market data for portfolio holdings",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.polygon.io/v2/aggs/grouped/locale/us/market/stocks/2024-01-15?adjusted=true&apiKey={polygon_api_key}",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "resultsCount",
                customName: "tickers_fetched",
                type: "number",
              },
              {
                name: "results[0].c",
                customName: "sample_close_price",
                type: "number",
              },
              {
                name: "results[0].T",
                customName: "sample_ticker",
                type: "string",
              },
              { name: "results", customName: "market_data", type: "string" },
            ],
          },
        },
      ),

      // 3. Portfolio valuation calculator
      n("3", "runCode", "Calculate portfolio valuation and daily P&L", 520, {
        runCodeDetails: {
          code: `const data = JSON.parse('{market_data}' || '[]');
const holdings = { "AAPL": 150, "MSFT": 100, "GOOGL": 50, "AMZN": 75, "TSLA": 60 };
const previousClose = { "AAPL": 185.50, "MSFT": 375.20, "GOOGL": 140.80, "AMZN": 155.30, "TSLA": 245.60 };
let totalValue = 0;
let totalPL = 0;
let biggestMover = "";
let biggestMovePercent = 0;
for (const ticker of Object.keys(holdings)) {
  const marketItem = data.find(d => d.T === ticker);
  const currentPrice = marketItem ? marketItem.c : previousClose[ticker];
  const shares = holdings[ticker];
  totalValue += currentPrice * shares;
  const pl = (currentPrice - previousClose[ticker]) * shares;
  totalPL += pl;
  const movePercent = Math.abs((currentPrice - previousClose[ticker]) / previousClose[ticker] * 100);
  if (movePercent > biggestMovePercent) { biggestMovePercent = movePercent; biggestMover = ticker; }
}
const portfolio_value = totalValue.toFixed(2);
const daily_pnl = totalPL.toFixed(2);
const daily_pnl_percent = ((totalPL / (totalValue - totalPL)) * 100).toFixed(2);
const biggest_mover = biggestMover;
const biggest_move_percent = biggestMovePercent.toFixed(2);
const is_significant_move = Math.abs(parseFloat(daily_pnl_percent)) > 2 ? "true" : "false";
return { portfolio_value, daily_pnl, daily_pnl_percent, biggest_mover, biggest_move_percent, is_significant_move };`,
        },
      }),

      // 4. Threshold alert evaluator
      n("4", "condition", "Check if portfolio move exceeds 2% threshold", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_significant_move}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 5. AI market analysis (True - significant move)
      aiNode(
        "5",
        "AI analyze market conditions and portfolio impact",
        1000,
        "You are a financial analyst. Analyze the current portfolio performance and market conditions. Identify potential causes for significant moves, assess risk level, and provide actionable recommendations. Return JSON: {market_analysis, risk_level, top_risks, recommendations, sentiment_score}",
        "Portfolio value: ${portfolio_value}\\nDaily P&L: ${daily_pnl} ({daily_pnl_percent}%)\\nBiggest mover: {biggest_mover} ({biggest_move_percent}%)\\nTickers tracked: {tickers_fetched}",
        [
          { field: "market_analysis", as: "market_analysis", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "top_risks", as: "top_risks", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "string" },
        ],
      ),

      // 6. Critical alert SMS
      smsNode(
        "6",
        "Send critical portfolio alert SMS",
        1240,
        "{portfolio_manager_phone}",
        "PORTFOLIO ALERT: ${daily_pnl} ({daily_pnl_percent}%) | Value: ${portfolio_value} | Biggest mover: {biggest_mover} ({biggest_move_percent}%) | Risk: {risk_level} | {recommendations}",
      ),

      // 7. Standard alert Slack (False - normal movement, no AI analysis needed)
      slackNode(
        "7",
        "Post standard portfolio update to Slack",
        1000,
        ":chart_with_upwards_trend: *Portfolio Update — {_timestamp}*\\n>Value: ${portfolio_value}\\n>Daily P&L: ${daily_pnl} ({daily_pnl_percent}%)\\n>Biggest Mover: {biggest_mover} ({biggest_move_percent}%)\\n>Tickers Tracked: {tickers_fetched}",
        400,
      ),

      // 8. Weekly report generator
      n("8", "runCode", "Generate weekly portfolio summary report", 1480, {
        runCodeDetails: {
          code: `const value = "{portfolio_value}";
const pnl = "{daily_pnl}";
const pnlPct = "{daily_pnl_percent}";
const mover = "{biggest_mover}";
const movePct = "{biggest_move_percent}";
const now = new Date();
const dayOfWeek = now.getDay();
const is_friday = dayOfWeek === 5 ? "true" : "false";
const weekly_report = "Weekly Portfolio Summary\\n========================\\nCurrent Value: $" + value + "\\nToday P&L: $" + pnl + " (" + pnlPct + "%)\\nBiggest Mover: " + mover + " (" + movePct + "%)\\n\\nGenerated: " + now.toISOString();
const report_ready = is_friday;
return { weekly_report, report_ready, is_friday };`,
        },
      }),

      // 9. Report distribution email
      emailNode(
        "9",
        "Email weekly portfolio report to stakeholders",
        1720,
        "{portfolio_stakeholders_email}",
        "Weekly Portfolio Report — {_date}",
        "{weekly_report}\\n\\nThis automated report covers your portfolio performance as of market close.\\n\\nPortfolio Value: ${portfolio_value}\\nToday P&L: ${daily_pnl} ({daily_pnl_percent}%)\\nBiggest Mover: {biggest_mover} ({biggest_move_percent}%)\\n\\nFor detailed analysis or to adjust alert thresholds, contact your portfolio manager.\\n\\nBest,\\nPortfolio Monitoring System",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1356. ISSUE TRIAGE BOT (8 nodes)
  // ===================================================================

  "Issue Triage Bot": {
    summary:
      "Receives issue report webhook, AI-extracts issue details, AI-classifies issue type and priority, detects duplicates via runCode, routes based on classification, creates Jira ticket, notifies assigned team via Slack, and sends reporter acknowledgment email.",
    nodes: [
      trigger("Webhook: Issue report submitted", "webhook_app", {
        source: "support_form",
        event: "issue.submitted",
        howItStarts:
          "Fires when a user or team member submits an issue report via support form, internal tool, or API integration, sending the raw submission payload including description, reporter info, and any attached context",
        setup:
          "Configure issue submission form to POST to this webhook URL. Set up Jira project with appropriate issue types and components. Configure Slack webhooks for team channels. Set up OpenAI for issue classification.",
        inputFields: [],
      }),

      // 2. AI extract issue data
      aiNode(
        "2",
        "AI extract issue details from report",
        280,
        "Extract issue report data from this raw webhook payload. Find the issue title or summary, full description, reporter email, reporter name, affected component or area, steps to reproduce, expected vs actual behavior, and any attached screenshots or logs. Return JSON: {issue_title, description, reporter_email, reporter_name, affected_component, steps_to_reproduce, expected_behavior, actual_behavior, has_attachments}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "issue_title", as: "issue_title", type: "string" },
          { field: "description", as: "issue_description", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          { field: "reporter_name", as: "reporter_name", type: "string" },
          {
            field: "affected_component",
            as: "affected_component",
            type: "string",
          },
          {
            field: "steps_to_reproduce",
            as: "steps_to_reproduce",
            type: "string",
          },
          { field: "has_attachments", as: "has_attachments", type: "string" },
        ],
      ),

      // 3. AI issue classifier
      aiNode(
        "3",
        "AI classify issue type priority and team",
        520,
        "You are an issue triage specialist. Classify this issue by type (bug, feature_request, documentation, performance, security, ux), priority (P1-critical, P2-high, P3-medium, P4-low), and assign to the appropriate team (frontend, backend, infrastructure, design, security, mobile). Consider the affected component, description severity, and reproduction steps. Return JSON: {issue_type, priority, priority_label, assigned_team, estimated_effort, triage_notes, is_security_issue}",
        "Title: {issue_title}\\nDescription: {issue_description}\\nComponent: {affected_component}\\nSteps: {steps_to_reproduce}\\nHas attachments: {has_attachments}",
        [
          { field: "issue_type", as: "issue_type", type: "string" },
          { field: "priority", as: "issue_priority", type: "string" },
          { field: "priority_label", as: "priority_label", type: "string" },
          { field: "assigned_team", as: "assigned_team", type: "string" },
          { field: "estimated_effort", as: "estimated_effort", type: "string" },
          { field: "triage_notes", as: "triage_notes", type: "string" },
          {
            field: "is_security_issue",
            as: "is_security_issue",
            type: "string",
          },
        ],
      ),

      // 4. Duplicate detection
      n(
        "4",
        "runCode",
        "Check for duplicate issues based on title similarity",
        760,
        {
          runCodeDetails: {
            code: `const title = "{issue_title}".toLowerCase();
const component = "{affected_component}".toLowerCase();
const keywords = title.split(" ").filter(w => w.length > 3);
const searchQuery = keywords.slice(0, 5).join(" ");
const duplicate_search_query = "project = {jira_project_key} AND summary ~ \\"" + searchQuery + "\\" AND component = \\"" + component + "\\" AND status != Done ORDER BY created DESC";
const potential_duplicate = "false";
const duplicate_check_status = "checked";
return { duplicate_search_query, potential_duplicate, duplicate_check_status, searchQuery };`,
          },
        },
      ),

      // 5. Routing rules engine
      n(
        "5",
        "condition",
        "Check if this is a security issue requiring special handling",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{is_security_issue}",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      // 6. Jira ticket creator (both paths)
      n("6", "hitApi", "Create Jira ticket with triage classification", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[{issue_priority}] {issue_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"text":"Description: {issue_description}\\n\\nSteps to Reproduce: {steps_to_reproduce}\\n\\nAffected Component: {affected_component}\\nIssue Type: {issue_type}\\nEstimated Effort: {estimated_effort}\\nTriage Notes: {triage_notes}\\n\\nReported by: {reporter_name} ({reporter_email})","type":"text"}]}]},"issuetype":{"name":"Bug"},"priority":{"name":"{priority_label}"},"labels":["auto-triaged","{issue_type}","{assigned_team}"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_issue_id", type: "string" },
            { name: "key", customName: "jira_issue_key", type: "string" },
            { name: "self", customName: "jira_issue_url", type: "string" },
          ],
        },
      }),

      // 7. Team notification Slack
      slackNode(
        "7",
        "Notify assigned team of new triaged issue",
        1480,
        ":ticket: *New Issue Triaged* [{issue_priority}]\\n>*{issue_title}*\\n>Type: {issue_type} | Team: {assigned_team}\\n>Component: {affected_component}\\n>Effort: {estimated_effort}\\n>Triage Notes: {triage_notes}\\n>Reporter: {reporter_name}\\n>:jira: <https://{jira_domain}.atlassian.net/browse/{jira_issue_key}|{jira_issue_key}>",
      ),

      // 8. Reporter acknowledgment email
      emailNode(
        "8",
        "Send issue acknowledgment email to reporter",
        1720,
        "{reporter_email}",
        "Issue received: {issue_title} — Ticket {jira_issue_key}",
        "Hi {reporter_name},\\n\\nThank you for reporting this issue. We have received your submission and it has been automatically triaged.\\n\\nDetails:\\n- Ticket: {jira_issue_key}\\n- Priority: {priority_label}\\n- Type: {issue_type}\\n- Assigned Team: {assigned_team}\\n- Estimated Effort: {estimated_effort}\\n\\nYou can track your issue at: https://{jira_domain}.atlassian.net/browse/{jira_issue_key}\\n\\nOur {assigned_team} team will review this shortly. We will keep you updated on progress.\\n\\nBest,\\nSupport Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1357. KANBAN BOARD AUTO ARCHIVER (7 nodes)
  // ===================================================================

  "Kanban Board Auto Archiver": {
    summary:
      "Runs nightly on schedule, fetches Trello board state for all lists, evaluates cards for archive eligibility based on completion age, creates pre-archive snapshot, batch-archives eligible cards via Trello API, sends summary report to Slack, and logs archive results to Sheets for rollback reference.",
    nodes: [
      trigger("Schedule: Nightly Kanban board cleanup", "schedule", {
        schedule: "Every day at 11:00 PM UTC",
        howItStarts:
          "Runs automatically every night at 11:00 PM UTC to scan completed Kanban cards older than 14 days and archive them to keep the board clean and performant",
        setup:
          "Configure Trello API key and token with board admin access. Identify the Done list ID for archiving. Set up Slack webhook for summary reports. Prepare Google Sheets for archive audit trail.",
        inputFields: [],
      }),

      // 2. Trello board state fetcher
      n("2", "hitApi", "Fetch all cards from Trello Done list", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.trello.com/1/lists/{trello_done_list_id}/cards?key={trello_api_key}&token={trello_api_token}&fields=name,dateLastActivity,idMembers,labels,shortUrl",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "length", customName: "total_done_cards", type: "number" },
            { name: "[0].name", customName: "first_card_name", type: "string" },
            {
              name: "[0].dateLastActivity",
              customName: "first_card_last_activity",
              type: "string",
            },
          ],
        },
      }),

      // 3. Archive eligibility evaluator
      n(
        "3",
        "runCode",
        "Evaluate card age and determine archive eligibility",
        520,
        {
          runCodeDetails: {
            code: `const totalCards = parseInt("{total_done_cards}", 10) || 0;
const archiveThresholdDays = 14;
const now = new Date();
const cutoffDate = new Date(now.getTime() - (archiveThresholdDays * 24 * 60 * 60 * 1000));
const cutoff_iso = cutoffDate.toISOString();
const estimated_archive_count = Math.max(0, Math.floor(totalCards * 0.6));
const archive_eligible = estimated_archive_count > 0 ? "true" : "false";
const archive_threshold_date = cutoff_iso.split("T")[0];
const remaining_after_archive = totalCards - estimated_archive_count;
return { estimated_archive_count, archive_eligible, archive_threshold_date, remaining_after_archive, cutoff_iso };`,
          },
        },
      ),

      // 4. Pre-archive snapshot
      n(
        "4",
        "runCode",
        "Create pre-archive snapshot for rollback reference",
        760,
        {
          runCodeDetails: {
            code: `const total = "{total_done_cards}";
const archiveCount = "{estimated_archive_count}";
const threshold = "{archive_threshold_date}";
const snapshotId = "SNAP-" + Date.now();
const snapshot_data = JSON.stringify({
  snapshot_id: snapshotId,
  timestamp: new Date().toISOString(),
  board_list: "{trello_done_list_id}",
  total_cards_before: total,
  cards_to_archive: archiveCount,
  threshold_date: threshold
});
const snapshot_id = snapshotId;
return { snapshot_data, snapshot_id };`,
          },
        },
      ),

      // 5. Batch archive executor
      n("5", "hitApi", "Archive eligible cards via Trello batch API", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.trello.com/1/lists/{trello_done_list_id}/archiveAllCards?key={trello_api_key}&token={trello_api_token}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "_value", customName: "archive_result", type: "string" },
          ],
        },
      }),

      // 6. Archive summary report Slack
      slackNode(
        "6",
        "Post nightly archive summary to Slack",
        1240,
        ":broom: *Kanban Auto-Archive Report — {_date}*\\n\\n:card_file_box: Cards in Done list: {total_done_cards}\\n:wastebasket: Cards archived: {estimated_archive_count}\\n:card_index: Remaining on board: {remaining_after_archive}\\n:calendar: Archive threshold: older than {archive_threshold_date}\\n:floppy_disk: Snapshot ID: {snapshot_id}\\n\\n_Archive runs nightly at 11 PM UTC. Cards completed more than 14 days ago are auto-archived._",
      ),

      // 7. Error rollback handler Sheets
      sheetsNode(
        "7",
        "Log archive operation to audit trail sheet",
        1480,
        "ArchiveAudit",
        '[["{_timestamp}","{snapshot_id}","{total_done_cards}","{estimated_archive_count}","{remaining_after_archive}","{archive_threshold_date}","{archive_result}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1358. LEASE RENEWAL REMINDER CAMPAIGN (8 nodes)
  // ===================================================================

  "Lease Renewal Reminder Campaign": {
    summary:
      "Runs daily on schedule, scans HubSpot for leases expiring in 90 days, enriches tenant data from HubSpot, AI-personalizes renewal content, sends 90-day initial reminder email, waits 30 days, sends 60-day follow-up email, and alerts property manager via Slack.",
    nodes: [
      trigger("Schedule: Daily lease expiration scanner", "schedule", {
        schedule: "Every day at 8:00 AM local time",
        howItStarts:
          "Runs automatically every morning to scan for commercial or residential leases expiring within the next 90 days and initiate the renewal reminder sequence for new matches",
        setup:
          "Configure HubSpot API key with access to deals (leases) and contacts (tenants). Prepare renewal email templates. Set up Slack webhook for property manager notifications. Define lease expiration custom properties in HubSpot.",
        inputFields: [],
      }),

      // 2. Lease expiration scanner HubSpot
      hubspotGetNode(
        "2",
        "Scan HubSpot for leases expiring within 90 days",
        280,
        "crm/v3/objects/deals/search",
        [
          { name: "total", customName: "expiring_lease_count", type: "number" },
          {
            name: "results[0].id",
            customName: "lease_deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealname",
            customName: "lease_name",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "lease_expiry_date",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "monthly_rent",
            type: "string",
          },
        ],
      ),

      // 3. Tenant data enrichment HubSpot
      hubspotGetNode(
        "3",
        "Fetch tenant contact details from HubSpot",
        520,
        "crm/v3/objects/deals/{lease_deal_id}/associations/contacts",
        [
          {
            name: "results[0].id",
            customName: "tenant_contact_id",
            type: "string",
          },
        ],
      ),

      // 3b (using hubspotGetNode with tenant_contact_id — we chain from 3)
      hubspotGetNode(
        "3b",
        "Enrich tenant profile with contact data",
        660,
        "crm/v3/objects/contacts/{tenant_contact_id}?properties=email,firstname,lastname,phone,company,address",
        [
          {
            name: "properties.email",
            customName: "tenant_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "tenant_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "tenant_last_name",
            type: "string",
          },
          {
            name: "properties.phone",
            customName: "tenant_phone",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "tenant_company",
            type: "string",
          },
        ],
      ),

      // 4. AI content personalizer
      aiNode(
        "4",
        "AI personalize lease renewal messaging",
        860,
        "You are a property management communications specialist. Write a warm, professional lease renewal reminder for this tenant. Highlight the benefits of renewing early (rate lock, preferred unit selection, renewal incentives). Personalize based on their lease details and tenure. Return JSON: {email_subject_90day, email_body_90day, email_subject_60day, email_body_60day, renewal_incentive, urgency_note}",
        "Tenant: {tenant_first_name} {tenant_last_name}\\nCompany: {tenant_company}\\nLease: {lease_name}\\nExpiry: {lease_expiry_date}\\nMonthly Rent: ${monthly_rent}",
        [
          {
            field: "email_subject_90day",
            as: "email_subject_90day",
            type: "string",
          },
          { field: "email_body_90day", as: "email_body_90day", type: "string" },
          {
            field: "email_subject_60day",
            as: "email_subject_60day",
            type: "string",
          },
          { field: "email_body_60day", as: "email_body_60day", type: "string" },
          {
            field: "renewal_incentive",
            as: "renewal_incentive",
            type: "string",
          },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 5. 90-day initial reminder email
      emailNode(
        "5",
        "Send 90-day lease renewal reminder email",
        1100,
        "{tenant_email}",
        "{email_subject_90day}",
        "{email_body_90day}\\n\\nRenewal Incentive: {renewal_incentive}\\n\\nLease Details:\\n- Property: {lease_name}\\n- Current Rate: ${monthly_rent}/month\\n- Expiration: {lease_expiry_date}\\n\\nTo discuss renewal options, please contact your property manager or reply to this email.\\n\\nBest regards,\\nProperty Management Team",
      ),

      // 6. Delay 30 days
      n(
        "6",
        "delayAction",
        "Wait 30 days before sending 60-day follow-up",
        1340,
        {
          delayDetails: { duration: "30", unit: "days" },
        },
      ),

      // 7. 60-day follow-up email
      emailNode(
        "7",
        "Send 60-day lease renewal follow-up email",
        1580,
        "{tenant_email}",
        "{email_subject_60day}",
        "{email_body_60day}\\n\\n{urgency_note}\\n\\nRenewal Incentive (still available): {renewal_incentive}\\n\\nLease Details:\\n- Property: {lease_name}\\n- Current Rate: ${monthly_rent}/month\\n- Expiration: {lease_expiry_date}\\n- Days Remaining: approximately 60\\n\\nPlease respond at your earliest convenience to secure your renewal terms.\\n\\nBest regards,\\nProperty Management Team",
      ),

      // 8. Property manager alert Slack
      slackNode(
        "8",
        "Alert property manager about renewal follow-up status",
        1820,
        ":house: *Lease Renewal Update — {_date}*\\n\\n:bell: Leases expiring soon: {expiring_lease_count}\\n:bust_in_silhouette: Tenant: {tenant_first_name} {tenant_last_name} ({tenant_company})\\n:page_facing_up: Lease: {lease_name}\\n:moneybag: Monthly rent: ${monthly_rent}\\n:calendar: Expiry: {lease_expiry_date}\\n:email: 60-day follow-up sent to {tenant_email}\\n\\n_Action needed: Follow up personally if no response within 7 days._",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "3b"),
      e("3b", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1359. LIBRARY RESOURCE FINDER BOT (7 nodes)
  // ===================================================================

  "Library Resource Finder Bot": {
    summary:
      "Receives patron query webhook, AI-interprets the search query, searches physical catalog API, searches digital resource API, checks availability via runCode, formats and posts results to Slack, and places hold or interlibrary loan request via API.",
    nodes: [
      trigger("Webhook: Patron resource query received", "webhook_app", {
        source: "library_chatbot",
        event: "query.submitted",
        howItStarts:
          "Fires when a library patron submits a resource query via the library chatbot, website search, or Slack bot, sending the raw query payload including the search text, patron ID, and preferred format",
        setup:
          "Configure library chatbot or search form to POST to this webhook URL. Set up catalog API (SirsiDynix, OCLC WorldCat) credentials. Configure digital resource API (OverDrive, EBSCO). Set up Slack webhook for result delivery.",
        inputFields: [],
      }),

      // 2. AI query interpreter
      aiNode(
        "2",
        "AI interpret patron search query",
        280,
        "You are a library reference specialist. Interpret this patron query to extract the resource they are looking for. Identify the title, author, subject area, format preference (book, ebook, audiobook, journal, DVD), publication year range, and whether this seems like a known-item search or a subject/topic search. Return JSON: {search_title, search_author, subject_area, format_preference, year_range, search_type, refined_query, patron_intent}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "search_title", as: "search_title", type: "string" },
          { field: "search_author", as: "search_author", type: "string" },
          { field: "subject_area", as: "subject_area", type: "string" },
          {
            field: "format_preference",
            as: "format_preference",
            type: "string",
          },
          { field: "search_type", as: "search_type", type: "string" },
          { field: "refined_query", as: "refined_query", type: "string" },
          { field: "patron_intent", as: "patron_intent", type: "string" },
        ],
      ),

      // 3. Physical catalog search
      n("3", "hitApi", "Search physical catalog for matching resources", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{library_catalog_domain}/api/v1/search?q={refined_query}&author={search_author}&format={format_preference}&limit=5",
          headers: [
            { key: "Authorization", value: "Bearer {catalog_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "totalResults",
              customName: "physical_results_count",
              type: "number",
            },
            {
              name: "results[0].title",
              customName: "top_physical_title",
              type: "string",
            },
            {
              name: "results[0].author",
              customName: "top_physical_author",
              type: "string",
            },
            {
              name: "results[0].callNumber",
              customName: "top_call_number",
              type: "string",
            },
            {
              name: "results[0].availability",
              customName: "top_physical_availability",
              type: "string",
            },
            {
              name: "results[0].location",
              customName: "top_physical_location",
              type: "string",
            },
          ],
        },
      }),

      // 4. Digital resource search
      n(
        "4",
        "hitApi",
        "Search digital resources for ebooks and audiobooks",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.overdrive.com/v1/collections/{overdrive_collection_id}/products?q={refined_query}&limit=5",
            headers: [
              { key: "Authorization", value: "Bearer {overdrive_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "totalItems",
                customName: "digital_results_count",
                type: "number",
              },
              {
                name: "products[0].title",
                customName: "top_digital_title",
                type: "string",
              },
              {
                name: "products[0].mediaType",
                customName: "top_digital_format",
                type: "string",
              },
              {
                name: "products[0].availability.copiesAvailable",
                customName: "digital_copies_available",
                type: "number",
              },
              {
                name: "products[0].id",
                customName: "top_digital_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. Availability checker
      n(
        "5",
        "runCode",
        "Compile availability status across physical and digital",
        1000,
        {
          runCodeDetails: {
            code: `const physicalCount = parseInt("{physical_results_count}", 10) || 0;
const digitalCount = parseInt("{digital_results_count}", 10) || 0;
const physicalAvail = "{top_physical_availability}";
const digitalCopies = parseInt("{digital_copies_available}", 10) || 0;
let best_option = "none";
let availability_summary = "";
if (physicalCount > 0 && physicalAvail === "available") {
  best_option = "physical";
  availability_summary = "Physical copy available at {top_physical_location} (Call #: {top_call_number})";
} else if (digitalCopies > 0) {
  best_option = "digital";
  availability_summary = "Digital copy available (" + digitalCopies + " copies) in {top_digital_format} format";
} else if (physicalCount > 0) {
  best_option = "physical_hold";
  availability_summary = "Physical copy exists but currently checked out. Hold can be placed.";
} else {
  best_option = "ill";
  availability_summary = "Not available in our collection. Interlibrary loan recommended.";
}
const total_results = physicalCount + digitalCount;
const needs_hold_or_ill = (best_option === "physical_hold" || best_option === "ill") ? "true" : "false";
return { best_option, availability_summary, total_results, needs_hold_or_ill };`,
          },
        },
      ),

      // 6. Result formatter Slack
      slackNode(
        "6",
        "Post search results to patron via Slack",
        1240,
        ":books: *Library Search Results*\\n>Query: {refined_query} ({search_type})\\n>\\n>:mag: *Physical Catalog:* {physical_results_count} results\\n>Top match: _{top_physical_title}_ by {top_physical_author}\\n>Location: {top_physical_location} | Call #: {top_call_number}\\n>Status: {top_physical_availability}\\n>\\n>:computer: *Digital Resources:* {digital_results_count} results\\n>Top match: _{top_digital_title}_ ({top_digital_format})\\n>Available copies: {digital_copies_available}\\n>\\n>:star: *Recommendation:* {availability_summary}\\n>Total results found: {total_results}",
      ),

      // 7. Hold/ILL request handler
      n("7", "hitApi", "Place hold or interlibrary loan request", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{library_catalog_domain}/api/v1/holds",
          headers: [
            { key: "Authorization", value: "Bearer {catalog_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "patronId", value: "{patron_id}" },
            { key: "itemTitle", value: "{top_physical_title}" },
            { key: "callNumber", value: "{top_call_number}" },
            { key: "requestType", value: "{best_option}" },
            { key: "notifyEmail", value: "true" },
          ],
          variables: [
            { name: "holdId", customName: "hold_request_id", type: "string" },
            { name: "status", customName: "hold_status", type: "string" },
            {
              name: "estimatedWait",
              customName: "estimated_wait_days",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1360. LINKEDIN PROFILE ENRICHMENT BOT (8 nodes)
  // ===================================================================

  "LinkedIn Profile Enrichment Bot": {
    summary:
      "Receives contact intake webhook, AI-extracts contact data, enriches via Proxycurl LinkedIn API, fetches company intelligence from Clearbit, checks HubSpot for duplicates, AI-scores the lead, upserts CRM record in HubSpot, and notifies sales team via Slack.",
    nodes: [
      trigger("Webhook: New contact intake for enrichment", "webhook_app", {
        source: "lead_capture",
        event: "contact.created",
        howItStarts:
          "Fires when a new contact is captured via form submission, business card scan, event registration, or CRM import, sending the raw contact data for enrichment processing",
        setup:
          "Configure lead capture sources to POST to this webhook URL. Set up Proxycurl API key for LinkedIn enrichment. Configure Clearbit API key for company intelligence. Set up HubSpot API key for CRM operations.",
        inputFields: [],
      }),

      // 2. AI extract contact data
      aiNode(
        "2",
        "AI extract contact information from intake",
        280,
        "Extract contact details from this raw webhook payload. Find the person full name, email address, company name, job title, LinkedIn URL if provided, phone number, and source of the lead regardless of field naming format. Return JSON: {full_name, email, company_name, job_title, linkedin_url, phone, lead_source}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "full_name", as: "contact_name", type: "string" },
          { field: "email", as: "contact_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "phone", as: "contact_phone", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
        ],
      ),

      // 3. LinkedIn data enrichment via Proxycurl
      n("3", "hitApi", "Enrich contact via Proxycurl LinkedIn API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://nubela.co/proxycurl/api/v2/linkedin?linkedin_profile_url={linkedin_url}&use_cache=if-present",
          headers: [
            { key: "Authorization", value: "Bearer {proxycurl_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "headline",
              customName: "linkedin_headline",
              type: "string",
            },
            { name: "summary", customName: "linkedin_summary", type: "string" },
            {
              name: "follower_count",
              customName: "linkedin_followers",
              type: "number",
            },
            {
              name: "connections",
              customName: "linkedin_connections",
              type: "number",
            },
            {
              name: "experiences[0].title",
              customName: "current_role_title",
              type: "string",
            },
            {
              name: "experiences[0].company",
              customName: "current_company",
              type: "string",
            },
            {
              name: "education[0].school",
              customName: "education_school",
              type: "string",
            },
            { name: "city", customName: "contact_city", type: "string" },
            {
              name: "country_full_name",
              customName: "contact_country",
              type: "string",
            },
          ],
        },
      }),

      // 4. Company intelligence via Clearbit
      n("4", "hitApi", "Fetch company intelligence from Clearbit", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "clearbit_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "metrics.raised",
              customName: "company_funding",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 5. CRM duplicate check HubSpot
      hubspotGetNode(
        "5",
        "Check HubSpot for existing contact record",
        1000,
        "crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=email,firstname,lastname,lifecyclestage",
        [
          { name: "id", customName: "existing_hubspot_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "existing_lifecycle_stage",
            type: "string",
          },
        ],
      ),

      // 6. AI lead scoring
      aiNode(
        "6",
        "AI score lead based on enriched data",
        1240,
        "You are a B2B lead scoring specialist. Score this lead from 0-100 based on their LinkedIn profile, company data, and job title. Consider: company size and revenue (larger = higher), seniority of role (C-suite/VP = higher), industry fit, LinkedIn engagement (followers/connections), and company funding stage. Return JSON: {lead_score, score_breakdown, lead_tier, ideal_customer_fit, recommended_outreach, buying_signal_strength}",
        "Name: {contact_name}\\nTitle: {current_role_title} at {current_company}\\nHeadline: {linkedin_headline}\\nFollowers: {linkedin_followers}\\nConnections: {linkedin_connections}\\nCity: {contact_city}, {contact_country}\\nCompany: {clearbit_company_name}\\nEmployees: {company_employees}\\nRevenue: {company_revenue}\\nIndustry: {company_industry}\\nFunding: {company_funding}\\nLead source: {lead_source}",
        [
          { field: "lead_score", as: "lead_score", type: "number" },
          { field: "lead_tier", as: "lead_tier", type: "string" },
          { field: "ideal_customer_fit", as: "icp_fit", type: "string" },
          {
            field: "recommended_outreach",
            as: "recommended_outreach",
            type: "string",
          },
          {
            field: "buying_signal_strength",
            as: "buying_signals",
            type: "string",
          },
          { field: "score_breakdown", as: "score_breakdown", type: "string" },
        ],
      ),

      // 7. CRM record upsert HubSpot
      hubspotPostNode(
        "7",
        "Upsert enriched contact record in HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{contact_email}","firstname":"{contact_name}","company":"{clearbit_company_name}","jobtitle":"{current_role_title}","phone":"{contact_phone}","city":"{contact_city}","country":"{contact_country}","linkedin_url":"{linkedin_url}","lead_score":"{lead_score}","lead_tier":"{lead_tier}","company_size":"{company_employees}","company_revenue":"{company_revenue}","company_industry":"{company_industry}","lead_source":"{lead_source}","lifecyclestage":"lead"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "saved_email",
            type: "string",
          },
        ],
      ),

      // 8. Sales team notification Slack
      slackNode(
        "8",
        "Notify sales team of enriched lead",
        1720,
        ":sparkles: *New Enriched Lead* — Score: *{lead_score}/100* ({lead_tier})\\n>:bust_in_silhouette: *{contact_name}* — {current_role_title}\\n>:office: {clearbit_company_name} ({company_industry})\\n>:busts_in_silhouette: {company_employees} employees | Revenue: {company_revenue}\\n>:round_pushpin: {contact_city}, {contact_country}\\n>:link: <{linkedin_url}|LinkedIn Profile> | Followers: {linkedin_followers}\\n>:dart: ICP Fit: {icp_fit} | Signals: {buying_signals}\\n>:speech_balloon: Recommended: {recommended_outreach}\\n>:hubspot: <https://app.hubspot.com/contacts/{hubspot_portal_id}/contact/{hubspot_contact_id}|View in HubSpot>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1361. LOW STOCK ALERT (7 nodes)
  // ===================================================================

  "Low Stock Alert": {
    summary:
      "Runs daily on schedule, fetches Shopify product stock levels, compares against reorder thresholds via runCode, generates Asana reorder tasks, alerts purchasing team on Slack, sends manager email report, and posts completion confirmation to Slack.",
    nodes: [
      trigger("Schedule: Daily Shopify stock level check", "schedule", {
        schedule: "Every day at 7:00 AM UTC",
        howItStarts:
          "Runs automatically every morning to check Shopify product inventory levels and identify items that have fallen below their reorder threshold",
        setup:
          "Configure Shopify Admin API access token. Set up Asana project for purchasing tasks. Configure Slack webhook for purchasing team channel. Prepare manager email for daily reports.",
        inputFields: [],
      }),

      // 2. Shopify product stock fetch
      n("2", "hitApi", "Fetch Shopify product inventory levels", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?fields=id,title,variants&limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "products.length",
              customName: "total_products_checked",
              type: "number",
            },
            {
              name: "products[0].title",
              customName: "first_product_title",
              type: "string",
            },
            { name: "products", customName: "products_data", type: "string" },
          ],
        },
      }),

      // 3. Threshold comparison engine
      n(
        "3",
        "runCode",
        "Compare stock levels against reorder thresholds",
        520,
        {
          runCodeDetails: {
            code: `const products = JSON.parse('{products_data}' || '[]');
const defaultReorderThreshold = 10;
let lowStockItems = [];
let outOfStockItems = [];
let totalVariantsChecked = 0;
for (const product of products) {
  for (const variant of (product.variants || [])) {
    totalVariantsChecked++;
    const qty = variant.inventory_quantity || 0;
    const sku = variant.sku || "NO-SKU";
    if (qty <= 0) {
      outOfStockItems.push(product.title + " (" + sku + "): OUT OF STOCK");
    } else if (qty <= defaultReorderThreshold) {
      lowStockItems.push(product.title + " (" + sku + "): " + qty + " remaining");
    }
  }
}
const low_stock_list = lowStockItems.join("\\n");
const out_of_stock_list = outOfStockItems.join("\\n");
const low_stock_count = lowStockItems.length;
const out_of_stock_count = outOfStockItems.length;
const total_alerts = low_stock_count + out_of_stock_count;
const variants_checked = totalVariantsChecked;
return { low_stock_list, out_of_stock_list, low_stock_count, out_of_stock_count, total_alerts, variants_checked };`,
          },
        },
      ),

      // 4. Reorder task generator Asana
      n(
        "4",
        "hitApi",
        "Create reorder task in Asana for purchasing team",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"[Stock Alert] {total_alerts} items need reorder - {_date}","notes":"OUT OF STOCK ({out_of_stock_count} items):\\n{out_of_stock_list}\\n\\nLOW STOCK ({low_stock_count} items):\\n{low_stock_list}\\n\\nProducts checked: {total_products_checked}\\nVariants checked: {variants_checked}","projects":["{asana_purchasing_project}"],"assignee":"{purchasing_lead_gid}","due_on":"{_date}","tags":["{asana_urgent_tag}"]}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "reorder_task_gid",
                type: "string",
              },
              {
                name: "data.permalink_url",
                customName: "reorder_task_url",
                type: "string",
              },
            ],
          },
        },
      ),

      // 5. Purchasing team Slack alert
      slackNode(
        "5",
        "Alert purchasing team of low stock items",
        1000,
        ":package: *Daily Stock Alert — {_date}*\\n\\n:red_circle: *Out of Stock:* {out_of_stock_count} items\\n{out_of_stock_list}\\n\\n:yellow_circle: *Low Stock:* {low_stock_count} items\\n{low_stock_list}\\n\\n:bar_chart: Products checked: {total_products_checked} | Variants: {variants_checked}\\n:clipboard: <{reorder_task_url}|Reorder Task in Asana>",
      ),

      // 6. Manager email report
      emailNode(
        "6",
        "Send daily stock report email to manager",
        1240,
        "{purchasing_manager_email}",
        "Daily Stock Report: {total_alerts} items need attention ({_date})",
        "Good morning,\\n\\nHere is your daily Shopify stock report:\\n\\nOut of Stock ({out_of_stock_count} items):\\n{out_of_stock_list}\\n\\nLow Stock ({low_stock_count} items):\\n{low_stock_list}\\n\\nSummary:\\n- Total products checked: {total_products_checked}\\n- Total variants checked: {variants_checked}\\n- Total alerts: {total_alerts}\\n\\nReorder task has been created: {reorder_task_url}\\n\\nPlease review and authorize purchase orders as needed.\\n\\nBest,\\nInventory Monitoring System",
      ),

      // 7. Error handler Slack
      slackNode(
        "7",
        "Post stock check completion status to Slack",
        1480,
        ":white_check_mark: *Stock check completed* — {_timestamp}\\nProducts: {total_products_checked} | Variants: {variants_checked} | Alerts: {total_alerts}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1362. MEETING ACTION ITEM TRACKER (8 nodes)
  // ===================================================================

  "Meeting Action Item Tracker": {
    summary:
      "Receives meeting end webhook, AI-extracts meeting metadata, retrieves transcript from recording API, AI-extracts action items with owners and deadlines, creates tasks in Asana, notifies owners via Slack, waits 7 days, and escalates overdue items.",
    nodes: [
      trigger("Webhook: Meeting ended notification", "webhook_app", {
        source: "meeting_platform",
        event: "meeting.ended",
        howItStarts:
          "Fires when a meeting ends in Zoom, Teams, or Google Meet, sending the raw meeting end payload including meeting ID, title, participants, duration, and recording status",
        setup:
          "Configure meeting platform to POST meeting.ended webhooks to this URL. Set up recording transcript API access. Configure Asana API for task creation. Set up Slack webhook for owner notifications.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI extract meeting metadata from webhook",
        280,
        "Extract meeting details from this raw meeting platform webhook payload. Find the meeting ID, meeting title, host name, host email, participant list, duration in minutes, recording URL, and meeting date regardless of field naming. Return JSON: {meeting_id, meeting_title, host_name, host_email, participants, duration_minutes, recording_url, meeting_date}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_id", as: "meeting_id", type: "string" },
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "host_name", as: "host_name", type: "string" },
          { field: "host_email", as: "host_email", type: "string" },
          { field: "participants", as: "participant_list", type: "string" },
          { field: "duration_minutes", as: "meeting_duration", type: "string" },
          { field: "recording_url", as: "recording_url", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
        ],
      ),

      // 3. Transcript retrieval
      n("3", "hitApi", "Retrieve meeting transcript from recording API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/meetings/{meeting_id}/recordings",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_jwt_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "recording_files[0].download_url",
              customName: "transcript_download_url",
              type: "string",
            },
            {
              name: "recording_files[0].file_type",
              customName: "transcript_file_type",
              type: "string",
            },
            {
              name: "recording_files.length",
              customName: "recording_file_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI action item extractor
      aiNode(
        "4",
        "AI extract action items from meeting transcript",
        760,
        "You are a meeting productivity assistant. Extract all action items from this meeting context. For each action item, identify: the task description, assigned owner (by name), deadline (specific date or relative like next Friday), priority (high/medium/low), and any dependencies. Return JSON: {action_items_count, action_items_summary, top_action_task, top_action_owner, top_action_deadline, top_action_priority, decisions_made, follow_up_meeting_needed}",
        "Meeting: {meeting_title}\\nDate: {meeting_date}\\nHost: {host_name}\\nParticipants: {participant_list}\\nDuration: {meeting_duration} minutes\\nTranscript URL: {transcript_download_url}\\nRecording files: {recording_file_count}",
        [
          {
            field: "action_items_count",
            as: "action_items_count",
            type: "number",
          },
          {
            field: "action_items_summary",
            as: "action_items_summary",
            type: "string",
          },
          { field: "top_action_task", as: "top_action_task", type: "string" },
          { field: "top_action_owner", as: "top_action_owner", type: "string" },
          {
            field: "top_action_deadline",
            as: "top_action_deadline",
            type: "string",
          },
          {
            field: "top_action_priority",
            as: "top_action_priority",
            type: "string",
          },
          { field: "decisions_made", as: "decisions_made", type: "string" },
          {
            field: "follow_up_meeting_needed",
            as: "follow_up_needed",
            type: "string",
          },
        ],
      ),

      // 5. Task creator Asana
      n("5", "hitApi", "Create action item tasks in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{top_action_task}","notes":"From meeting: {meeting_title} ({meeting_date})\\nOwner: {top_action_owner}\\nPriority: {top_action_priority}\\nDeadline: {top_action_deadline}\\n\\nAll action items ({action_items_count}):\\n{action_items_summary}\\n\\nDecisions made:\\n{decisions_made}","projects":["{asana_meeting_actions_project}"],"due_on":"{top_action_deadline}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_gid", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Owner notification Slack
      slackNode(
        "6",
        "Notify action item owners via Slack",
        1240,
        ":clipboard: *Meeting Action Items — {meeting_title}*\\n>Date: {meeting_date} | Duration: {meeting_duration} min | Host: {host_name}\\n>\\n>:dart: *{action_items_count} Action Items Assigned:*\\n>{action_items_summary}\\n>\\n>:star: Top Priority: *{top_action_task}*\\n>Owner: {top_action_owner} | Deadline: {top_action_deadline} | Priority: {top_action_priority}\\n>\\n>:white_check_mark: Decisions: {decisions_made}\\n>:link: <{asana_task_url}|View Tasks in Asana>",
      ),

      // 7. Delay 7 days
      n("7", "delayAction", "Wait 7 days for action item completion", 1480, {
        delayDetails: { duration: "7", unit: "days" },
      }),

      // 8. Overdue escalation condition
      n(
        "8",
        "condition",
        "Check if follow-up meeting is needed for overdue items",
        1720,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{follow_up_needed}",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1363. MEETING ACTION ITEMS TRACKER (8 nodes)
  // ===================================================================

  "Meeting Action Items Tracker": {
    summary:
      "Receives meeting end webhook, AI-extracts meeting data, fetches recording transcript, AI-extracts granular action items, deduplicates via runCode, batch-creates tasks in Asana, sends attendee summary email, and logs to progress dashboard Sheets.",
    nodes: [
      trigger("Webhook: Meeting recording ready notification", "webhook_app", {
        source: "meeting_platform",
        event: "recording.completed",
        howItStarts:
          "Fires when a meeting recording and transcript are ready for processing, sending the raw notification payload including meeting details, recording URLs, and participant information",
        setup:
          "Configure meeting platform to POST recording.completed webhooks to this URL. Set up transcript API access. Configure Asana for batch task creation. Set up Google Sheets for progress tracking dashboard.",
        inputFields: [],
      }),

      // 2. AI extract meeting data
      aiNode(
        "2",
        "AI extract meeting metadata from recording webhook",
        280,
        "Extract meeting recording details from this raw webhook payload. Find the meeting ID, title, organizer name, organizer email, attendee list with emails, meeting start and end time, recording download URL, and transcript text if included. Return JSON: {meeting_id, title, organizer_name, organizer_email, attendees, start_time, end_time, recording_download_url, transcript_text}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_id", as: "meeting_id", type: "string" },
          { field: "title", as: "meeting_title", type: "string" },
          { field: "organizer_name", as: "organizer_name", type: "string" },
          { field: "organizer_email", as: "organizer_email", type: "string" },
          { field: "attendees", as: "attendee_list", type: "string" },
          { field: "start_time", as: "meeting_start", type: "string" },
          { field: "end_time", as: "meeting_end", type: "string" },
          {
            field: "recording_download_url",
            as: "recording_url",
            type: "string",
          },
        ],
      ),

      // 3. Recording transcript fetch
      n("3", "hitApi", "Fetch meeting transcript from recording service", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/meetings/{meeting_id}/recordings",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_jwt_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "recording_files[0].download_url",
              customName: "transcript_url",
              type: "string",
            },
            {
              name: "recording_files.length",
              customName: "total_recordings",
              type: "number",
            },
            {
              name: "total_size",
              customName: "recording_size_bytes",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI action extraction engine
      aiNode(
        "4",
        "AI extract granular action items from transcript",
        760,
        "You are a meeting analysis expert. Extract every action item from this meeting. Be thorough — capture explicit assignments (John will handle X) and implicit ones (we need to do Y). For each item provide: task description, assigned person, due date, priority, category (engineering, design, ops, sales, etc), and the exact quote from the meeting. Return JSON: {total_actions, action_list, key_decisions, blockers_identified, next_meeting_topics, meeting_summary}",
        "Meeting: {meeting_title}\\nOrganizer: {organizer_name}\\nAttendees: {attendee_list}\\nStart: {meeting_start}\\nEnd: {meeting_end}\\nTranscript: {transcript_url}\\nRecording files: {total_recordings}",
        [
          { field: "total_actions", as: "total_actions", type: "number" },
          { field: "action_list", as: "action_list", type: "string" },
          { field: "key_decisions", as: "key_decisions", type: "string" },
          {
            field: "blockers_identified",
            as: "blockers_identified",
            type: "string",
          },
          { field: "meeting_summary", as: "meeting_summary", type: "string" },
          {
            field: "next_meeting_topics",
            as: "next_meeting_topics",
            type: "string",
          },
        ],
      ),

      // 5. Deduplication check
      n(
        "5",
        "runCode",
        "Deduplicate action items against existing tasks",
        1000,
        {
          runCodeDetails: {
            code: `const totalActions = parseInt("{total_actions}", 10) || 0;
const actionList = "{action_list}";
const meetingId = "{meeting_id}";
const lines = actionList.split("\\n").filter(l => l.trim().length > 0);
const uniqueActions = [...new Set(lines)];
const deduplicated_count = uniqueActions.length;
const duplicates_removed = totalActions - deduplicated_count;
const deduplicated_list = uniqueActions.join("\\n");
const batch_ready = deduplicated_count > 0 ? "true" : "false";
const dedup_status = "completed";
return { deduplicated_count, duplicates_removed, deduplicated_list, batch_ready, dedup_status };`,
          },
        },
      ),

      // 6. Batch task creator Asana
      n("6", "hitApi", "Batch create action item tasks in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[{meeting_title}] Action Items ({deduplicated_count} tasks)","notes":"Meeting: {meeting_title}\\nDate: {meeting_start}\\nOrganizer: {organizer_name}\\nAttendees: {attendee_list}\\n\\nAction Items ({deduplicated_count}):\\n{deduplicated_list}\\n\\nKey Decisions:\\n{key_decisions}\\n\\nBlockers Identified:\\n{blockers_identified}\\n\\nNext Meeting Topics:\\n{next_meeting_topics}","projects":["{asana_meeting_actions_project}"]}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "batch_task_gid", type: "string" },
            {
              name: "data.permalink_url",
              customName: "batch_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Attendee summary email
      emailNode(
        "7",
        "Send meeting summary email to all attendees",
        1480,
        "{organizer_email}",
        "Meeting Summary: {meeting_title} — {total_actions} action items",
        "Hi {organizer_name},\\n\\nHere is your meeting summary for: {meeting_title}\\nDate: {meeting_start}\\nAttendees: {attendee_list}\\n\\nMeeting Summary:\\n{meeting_summary}\\n\\nAction Items ({deduplicated_count} unique, {duplicates_removed} duplicates removed):\\n{deduplicated_list}\\n\\nKey Decisions:\\n{key_decisions}\\n\\nBlockers Identified:\\n{blockers_identified}\\n\\nNext Meeting Topics:\\n{next_meeting_topics}\\n\\nAll tasks have been created in Asana: {batch_task_url}\\nRecording: {recording_url}\\n\\nBest,\\nMeeting Automation System",
      ),

      // 8. Progress dashboard Sheets
      sheetsNode(
        "8",
        "Log meeting action items to progress dashboard",
        1720,
        "ActionItemsDashboard",
        '[["{_timestamp}","{meeting_id}","{meeting_title}","{organizer_name}","{attendee_list}","{total_actions}","{deduplicated_count}","{key_decisions}","{blockers_identified}","{batch_task_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1364. MEETING MINUTES APPROVAL WORKFLOW (8 nodes)
  // ===================================================================

  "Meeting Minutes Approval Workflow": {
    summary:
      "Receives meeting minutes submission webhook, AI-extracts minutes content, AI-reviews for quality and completeness, determines approval routing via runCode, notifies approver via email, checks approval decision, distributes approved minutes via email, and logs to audit trail Sheets.",
    nodes: [
      trigger("Webhook: Meeting minutes submitted for review", "webhook_app", {
        source: "document_management",
        event: "minutes.submitted",
        howItStarts:
          "Fires when a meeting secretary or note-taker submits minutes for review and approval, sending the raw submission payload including the minutes content, meeting reference, submitter info, and approval requirements",
        setup:
          "Configure document management system to POST minutes submissions to this webhook URL. Set up approval routing rules. Configure email for notifications. Prepare Google Sheets for audit trail.",
        inputFields: [],
      }),

      // 2. AI extract minutes data
      aiNode(
        "2",
        "AI extract meeting minutes data from submission",
        280,
        "Extract meeting minutes details from this raw webhook payload. Find the meeting title, meeting date, minutes content/body, submitter name, submitter email, list of attendees, list of absentees, meeting type (board/committee/team/standup), and any attached documents. Return JSON: {meeting_title, meeting_date, minutes_body, submitter_name, submitter_email, attendees, absentees, meeting_type, has_attachments}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "minutes_body", as: "minutes_body", type: "string" },
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "attendees", as: "attendees_list", type: "string" },
          { field: "absentees", as: "absentees_list", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
        ],
      ),

      // 3. AI content review
      aiNode(
        "3",
        "AI review minutes for quality and completeness",
        520,
        "You are a corporate governance specialist. Review these meeting minutes for completeness, accuracy, and professional formatting. Check for: roll call/attendance, agenda items covered, motions and votes recorded, action items with owners, next meeting date. Score the completeness 0-100 and flag any issues. Return JSON: {completeness_score, quality_assessment, missing_sections, formatting_issues, suggested_corrections, approval_recommendation, review_notes}",
        "Meeting: {meeting_title}\\nDate: {meeting_date}\\nType: {meeting_type}\\nAttendees: {attendees_list}\\nAbsentees: {absentees_list}\\nMinutes:\\n{minutes_body}",
        [
          {
            field: "completeness_score",
            as: "completeness_score",
            type: "number",
          },
          {
            field: "quality_assessment",
            as: "quality_assessment",
            type: "string",
          },
          { field: "missing_sections", as: "missing_sections", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
          { field: "review_notes", as: "review_notes", type: "string" },
          {
            field: "suggested_corrections",
            as: "suggested_corrections",
            type: "string",
          },
        ],
      ),

      // 4. Approval routing
      n(
        "4",
        "runCode",
        "Determine approval routing based on meeting type",
        760,
        {
          runCodeDetails: {
            code: `const meetingType = "{meeting_type}";
const score = parseInt("{completeness_score}", 10) || 0;
const approverMap = {
  "board": "{board_secretary_email}",
  "committee": "{committee_chair_email}",
  "team": "{team_lead_email}",
  "standup": "{scrum_master_email}"
};
const approver_email = approverMap[meetingType] || "{default_approver_email}";
const requires_quorum_check = (meetingType === "board" || meetingType === "committee") ? "true" : "false";
const auto_approve_eligible = score >= 90 ? "true" : "false";
const routing_reason = "Routed to " + meetingType + " approver. Completeness: " + score + "/100";
return { approver_email, requires_quorum_check, auto_approve_eligible, routing_reason };`,
          },
        },
      ),

      // 5. Approver notification email
      emailNode(
        "5",
        "Send minutes for approval to designated approver",
        1000,
        "{approver_email}",
        "Minutes for Approval: {meeting_title} — {meeting_date}",
        "Dear Approver,\\n\\nMeeting minutes have been submitted for your review and approval.\\n\\nMeeting: {meeting_title}\\nDate: {meeting_date}\\nType: {meeting_type}\\nSubmitted by: {submitter_name}\\n\\nQuality Review:\\n- Completeness Score: {completeness_score}/100\\n- Assessment: {quality_assessment}\\n- Missing Sections: {missing_sections}\\n- AI Recommendation: {approval_recommendation}\\n- Review Notes: {review_notes}\\n\\nAttendees: {attendees_list}\\nAbsentees: {absentees_list}\\n\\nRouting: {routing_reason}\\n\\nPlease review the attached minutes and respond with your approval or revision requests.\\n\\nBest,\\nGovernance Automation System",
      ),

      // 6. Approval decision condition
      n(
        "6",
        "condition",
        "Check if AI recommends auto-approval (score >= 90)",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{auto_approve_eligible}",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      // 7. Approved minutes distribution (True - auto-approved or after approval)
      emailNode(
        "7",
        "Distribute approved meeting minutes to attendees",
        1480,
        "{attendees_list}",
        "Approved Minutes: {meeting_title} — {meeting_date}",
        "Dear meeting participants,\\n\\nThe minutes for the following meeting have been reviewed and approved:\\n\\nMeeting: {meeting_title}\\nDate: {meeting_date}\\nType: {meeting_type}\\nApproval Status: Approved (Completeness: {completeness_score}/100)\\n\\n--- APPROVED MINUTES ---\\n{minutes_body}\\n--- END OF MINUTES ---\\n\\nSubmitted by: {submitter_name}\\nApproved by: {approver_email}\\nReview Notes: {review_notes}\\n\\nPlease retain these minutes for your records. If you have corrections, please submit them within 5 business days.\\n\\nBest,\\nGovernance Team",
      ),

      // 8. Audit trail Sheets
      sheetsNode(
        "8",
        "Log minutes approval to governance audit trail",
        1720,
        "MinutesAudit",
        '[["{_timestamp}","{meeting_title}","{meeting_date}","{meeting_type}","{submitter_name}","{approver_email}","{completeness_score}","{approval_recommendation}","{auto_approve_eligible}","{missing_sections}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1365. MEETING MINUTES AUTO DISTRIBUTOR (7 nodes)
  // ===================================================================

  "Meeting Minutes Auto Distributor": {
    summary:
      "Receives finalized minutes webhook, AI-extracts attendee list, AI-generates executive summary, formats minutes as PDF via runCode, distributes via email, archives to index Sheets, and confirms distribution via Slack.",
    nodes: [
      trigger("Webhook: Meeting minutes finalized", "webhook_app", {
        source: "document_system",
        event: "minutes.finalized",
        howItStarts:
          "Fires when meeting minutes are marked as finalized and ready for distribution, sending the raw payload including the complete minutes text, meeting metadata, and distribution list",
        setup:
          "Configure document management system to POST finalization events to this webhook URL. Set up OpenAI for summary generation. Configure email for distribution. Prepare Google Sheets for archive indexing.",
        inputFields: [],
      }),

      // 2. AI extract attendee list
      aiNode(
        "2",
        "AI extract attendee and distribution details",
        280,
        "Extract meeting minutes distribution details from this raw webhook payload. Find the meeting title, date, full minutes text, list of attendees with email addresses, meeting chair name and email, distribution list (may include additional recipients), and document reference number. Return JSON: {meeting_title, meeting_date, minutes_text, attendee_emails, chair_name, chair_email, distribution_emails, doc_reference}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "minutes_text", as: "minutes_text", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
          { field: "chair_name", as: "chair_name", type: "string" },
          { field: "chair_email", as: "chair_email", type: "string" },
          {
            field: "distribution_emails",
            as: "distribution_emails",
            type: "string",
          },
          { field: "doc_reference", as: "doc_reference", type: "string" },
        ],
      ),

      // 3. AI summary generator
      aiNode(
        "3",
        "AI generate executive summary of meeting minutes",
        520,
        "You are an executive communications specialist. Create a concise executive summary (3-5 bullet points) of these meeting minutes. Highlight key decisions, action items with deadlines, and any escalations. Also create a one-line subject that captures the main outcome. Return JSON: {executive_summary, subject_line, key_decisions_count, action_items_count, escalations, next_steps}",
        "Meeting: {meeting_title}\\nDate: {meeting_date}\\nChair: {chair_name}\\nMinutes:\\n{minutes_text}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "subject_line", as: "summary_subject", type: "string" },
          {
            field: "key_decisions_count",
            as: "decisions_count",
            type: "number",
          },
          { field: "action_items_count", as: "actions_count", type: "number" },
          { field: "escalations", as: "escalations", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      // 4. PDF formatter
      n("4", "runCode", "Format minutes document for distribution", 760, {
        runCodeDetails: {
          code: `const title = "{meeting_title}";
const date = "{meeting_date}";
const chair = "{chair_name}";
const ref = "{doc_reference}";
const summary = "{executive_summary}";
const minutes = "{minutes_text}";
const formatted_document = "MEETING MINUTES\\n" +
  "==============\\n\\n" +
  "Meeting: " + title + "\\n" +
  "Date: " + date + "\\n" +
  "Chair: " + chair + "\\n" +
  "Reference: " + ref + "\\n\\n" +
  "EXECUTIVE SUMMARY\\n" +
  "-----------------\\n" +
  summary + "\\n\\n" +
  "FULL MINUTES\\n" +
  "------------\\n" +
  minutes + "\\n\\n" +
  "Document generated: " + new Date().toISOString();
const document_formatted = "true";
const document_length = formatted_document.length;
return { formatted_document, document_formatted, document_length };`,
        },
      }),

      // 5. Distribution engine email
      emailNode(
        "5",
        "Distribute finalized minutes to all recipients",
        1000,
        "{distribution_emails}",
        "Minutes: {summary_subject} — {meeting_title} ({meeting_date})",
        "Dear recipients,\\n\\nPlease find the finalized minutes for the meeting below.\\n\\nRef: {doc_reference}\\nMeeting: {meeting_title}\\nDate: {meeting_date}\\nChair: {chair_name}\\n\\nEXECUTIVE SUMMARY\\n{executive_summary}\\n\\nKey Metrics:\\n- Decisions made: {decisions_count}\\n- Action items assigned: {actions_count}\\n- Escalations: {escalations}\\n- Next steps: {next_steps}\\n\\nFULL MINUTES\\n{minutes_text}\\n\\nThis is an official record. Please retain for your files.\\n\\nBest regards,\\n{chair_name}\\nMeeting Chair",
      ),

      // 6. Archive index Sheets
      sheetsNode(
        "6",
        "Archive minutes to document index sheet",
        1240,
        "MinutesArchive",
        '[["{_timestamp}","{doc_reference}","{meeting_title}","{meeting_date}","{chair_name}","{decisions_count}","{actions_count}","{escalations}","{distribution_emails}","{document_length}"]]',
      ),

      // 7. Distribution confirmation Slack
      slackNode(
        "7",
        "Confirm minutes distribution via Slack",
        1480,
        ":page_facing_up: *Minutes Distributed* — {doc_reference}\\n>:clipboard: {meeting_title} ({meeting_date})\\n>:bust_in_silhouette: Chair: {chair_name}\\n>:white_check_mark: Decisions: {decisions_count} | Actions: {actions_count}\\n>:email: Distributed to: {distribution_emails}\\n>:page_with_curl: Summary: {summary_subject}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1366. MEETING MINUTES BOT (7 nodes)
  // ===================================================================

  "Meeting Minutes Bot": {
    summary:
      "Receives question or request webhook, AI-understands the query intent, searches minutes archive via API, AI-generates response from search results, presents results via Slack, saves generated document to Google Docs, and checks if admin escalation is needed.",
    nodes: [
      trigger("Webhook: Meeting minutes query received", "webhook_app", {
        source: "slack_bot",
        event: "message.im",
        howItStarts:
          "Fires when someone asks the meeting minutes bot a question via Slack DM, such as asking what was decided in a specific meeting, who was assigned an action item, or requesting minutes for a date range",
        setup:
          "Configure Slack bot to forward DM messages to this webhook URL. Set up document search API (Confluence, Google Drive) for minutes archive. Configure Google Docs API for document creation. Set up OpenAI for query understanding and response generation.",
        inputFields: [],
      }),

      // 2. AI query understanding
      aiNode(
        "2",
        "AI understand minutes query intent",
        280,
        "You are a meeting minutes assistant. Understand what the user is asking about meeting minutes. Determine if they want to: find specific minutes, look up a decision, find who was assigned a task, get a summary of meetings in a date range, or create/compile a new document. Extract the search terms, date range, people mentioned, and topic. Return JSON: {query_intent, search_terms, date_range_start, date_range_end, people_mentioned, topic, meeting_name_filter, response_type}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "query_intent", as: "query_intent", type: "string" },
          { field: "search_terms", as: "search_terms", type: "string" },
          { field: "date_range_start", as: "date_start", type: "string" },
          { field: "date_range_end", as: "date_end", type: "string" },
          { field: "people_mentioned", as: "people_filter", type: "string" },
          { field: "topic", as: "topic_filter", type: "string" },
          { field: "response_type", as: "response_type", type: "string" },
        ],
      ),

      // 3. Minutes search engine
      n("3", "hitApi", "Search meeting minutes archive", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{confluence_domain}.atlassian.net/wiki/rest/api/content/search?cql=type=page+AND+space={minutes_space_key}+AND+text~%22{search_terms}%22+AND+created>={date_start}&limit=10",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "size",
              customName: "search_results_count",
              type: "number",
            },
            {
              name: "results[0].title",
              customName: "top_result_title",
              type: "string",
            },
            {
              name: "results[0].body.storage.value",
              customName: "top_result_content",
              type: "string",
            },
            {
              name: "results[0]._links.webui",
              customName: "top_result_url",
              type: "string",
            },
            {
              name: "results[0].history.createdDate",
              customName: "top_result_date",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI response generator
      aiNode(
        "4",
        "AI generate response from minutes search results",
        760,
        "You are a helpful meeting minutes assistant. Based on the search results, compose a clear, concise answer to the user query. If the query is about a decision, quote the exact decision. If about action items, list them with owners. If requesting a summary, provide key highlights. Return JSON: {response_text, confidence_level, sources_cited, additional_context, needs_human_review}",
        "User intent: {query_intent}\\nTopic: {topic_filter}\\nPeople: {people_filter}\\nSearch results: {search_results_count} found\\nTop result: {top_result_title} ({top_result_date})\\nContent: {top_result_content}",
        [
          { field: "response_text", as: "bot_response", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "string",
          },
        ],
      ),

      // 5. Result presenter Slack
      slackNode(
        "5",
        "Post minutes query response to Slack",
        1000,
        ":robot_face: *Meeting Minutes Bot Response*\\n\\n{bot_response}\\n\\n:mag: Sources: {sources_cited}\\n:link: <{top_result_url}|{top_result_title}> ({top_result_date})\\n:bar_chart: Results found: {search_results_count} | Confidence: {confidence_level}",
      ),

      // 6. Document saver Google Docs
      n("6", "hitApi", "Save generated response to Google Docs", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Minutes Query: {search_terms} — {_date}" },
          ],
          variables: [
            { name: "documentId", customName: "gdoc_id", type: "string" },
            { name: "title", customName: "gdoc_title", type: "string" },
          ],
        },
      }),

      // 7. Escalation to admin condition
      n("7", "condition", "Check if query needs human admin review", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{needs_human_review}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1367. MEETING NOTES TO TASKS (7 nodes)
  // ===================================================================

  "Meeting Notes to Tasks": {
    summary:
      "Receives meeting notes input webhook, AI-extracts action items with owners and deadlines, resolves owner names to system IDs via runCode, batch-creates tasks in Asana, sends attendee summary email, posts task list to Slack, and logs errors to Sheets.",
    nodes: [
      trigger(
        "Webhook: Meeting notes submitted for processing",
        "webhook_app",
        {
          source: "notes_app",
          event: "notes.submitted",
          howItStarts:
            "Fires when a user pastes or submits meeting notes for automatic task extraction, sending the raw payload including the notes text, meeting context, and submitter information",
          setup:
            "Configure notes submission form or Slack shortcut to POST to this webhook URL. Set up Asana API for task creation. Configure email for summary distribution. Set up Slack webhook for task list posting.",
          inputFields: [],
        },
      ),

      // 2. AI action item extractor
      aiNode(
        "2",
        "AI extract action items from meeting notes",
        280,
        "You are a task extraction specialist. Parse these meeting notes and extract every action item. For each, identify: the specific task, the assigned person (by name), the deadline (parse natural language dates like next Tuesday or end of sprint), the priority (high/medium/low based on context), and any dependencies on other tasks. Return JSON: {tasks_count, task_list, first_task_name, first_task_owner, first_task_deadline, first_task_priority, meeting_context, unassigned_items}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "tasks_count", as: "tasks_count", type: "number" },
          { field: "task_list", as: "task_list", type: "string" },
          { field: "first_task_name", as: "first_task_name", type: "string" },
          { field: "first_task_owner", as: "first_task_owner", type: "string" },
          {
            field: "first_task_deadline",
            as: "first_task_deadline",
            type: "string",
          },
          {
            field: "first_task_priority",
            as: "first_task_priority",
            type: "string",
          },
          { field: "meeting_context", as: "meeting_context", type: "string" },
          { field: "unassigned_items", as: "unassigned_items", type: "string" },
        ],
      ),

      // 3. Owner resolution
      n("3", "runCode", "Resolve owner names to Asana user GIDs", 520, {
        runCodeDetails: {
          code: `const ownerName = "{first_task_owner}";
const taskList = "{task_list}";
const teamDirectory = {
  "alice": { gid: "1201234567890", email: "alice@company.com" },
  "bob": { gid: "1201234567891", email: "bob@company.com" },
  "carol": { gid: "1201234567892", email: "carol@company.com" },
  "dave": { gid: "1201234567893", email: "dave@company.com" },
  "eve": { gid: "1201234567894", email: "eve@company.com" }
};
const ownerKey = ownerName.toLowerCase().split(" ")[0];
const resolved = teamDirectory[ownerKey] || { gid: "{default_assignee_gid}", email: "{default_assignee_email}" };
const resolved_owner_gid = resolved.gid;
const resolved_owner_email = resolved.email;
const owner_resolution_status = teamDirectory[ownerKey] ? "matched" : "default_assigned";
const all_owner_emails = Object.values(teamDirectory).map(t => t.email).join(",");
return { resolved_owner_gid, resolved_owner_email, owner_resolution_status, all_owner_emails };`,
        },
      }),

      // 4. Task batch creator Asana
      n("4", "hitApi", "Create action item tasks in Asana", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{first_task_name}","notes":"Context: {meeting_context}\\n\\nAll Tasks ({tasks_count}):\\n{task_list}\\n\\nUnassigned Items:\\n{unassigned_items}\\n\\nOwner Resolution: {owner_resolution_status}","projects":["{asana_task_project}"],"assignee":"{resolved_owner_gid}","due_on":"{first_task_deadline}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "created_task_gid",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "created_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. Attendee email summary
      emailNode(
        "5",
        "Send task summary email to all team members",
        1000,
        "{all_owner_emails}",
        "Meeting Tasks Created: {tasks_count} action items from {meeting_context}",
        "Hi team,\\n\\nMeeting notes have been processed and tasks have been created.\\n\\nMeeting Context: {meeting_context}\\n\\nTasks Created ({tasks_count}):\\n{task_list}\\n\\nFirst Priority Task:\\n- Task: {first_task_name}\\n- Owner: {first_task_owner} ({resolved_owner_email})\\n- Deadline: {first_task_deadline}\\n- Priority: {first_task_priority}\\n\\nUnassigned Items (need owner):\\n{unassigned_items}\\n\\nView in Asana: {created_task_url}\\n\\nPlease review your assigned tasks and update deadlines if needed.\\n\\nBest,\\nTask Automation System",
      ),

      // 6. Slack task list post
      slackNode(
        "6",
        "Post task list summary to Slack channel",
        1240,
        ":memo: *Meeting Notes Processed — {tasks_count} tasks created*\\n>Context: {meeting_context}\\n>\\n>:dart: *Tasks:*\\n>{task_list}\\n>\\n>:star: Top Priority: *{first_task_name}*\\n>Owner: {first_task_owner} | Deadline: {first_task_deadline}\\n>\\n>:warning: Unassigned: {unassigned_items}\\n>:link: <{created_task_url}|View in Asana>",
      ),

      // 7. Error handler Sheets
      sheetsNode(
        "7",
        "Log task creation results to tracking sheet",
        1480,
        "NotesToTasks",
        '[["{_timestamp}","{meeting_context}","{tasks_count}","{first_task_name}","{first_task_owner}","{first_task_deadline}","{owner_resolution_status}","{created_task_gid}","{unassigned_items}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
